import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ChangeEventHandler,
} from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  EyeOff,
  FileSpreadsheet,
  Filter,
  GitCompareArrows,
  Globe,
  History,
  Home,
  LayoutTemplate,
  Link,
  Lock,
  MoreVertical,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Play,
  Plus,
  Printer,
  RefreshCcw,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import type {
  ReportGenerationAdvancedVisualizationBodySurface,
  ReportGenerationExportFormat,
  ReportGenerationSurfaceTheme,
  ReportGenerationWorkspaceModel,
} from "./types";
import { ReportGenerationAdvancedVisualization } from "./ReportGenerationAdvancedVisualization";
import { ReportGenerationPreviewLoadingArtifact } from "./ReportGenerationPreviewLoadingArtifact";
import {
  AssistantGoogleSheetsExportActionItem,
  openAssistantGoogleSheetsExportAction,
  resolveAssistantGoogleSheetsExportAction,
} from "../AssistantPresentationRenderer";
import { resolveAssistantArtifactDownloadFileName } from "../../utils/assistantArtifactDownload";

export type ReportGenerationWorkspaceProps = {
  model: ReportGenerationWorkspaceModel;
  surfaceTheme?: ReportGenerationSurfaceTheme;
  insightPresentation?: "cards" | "info-strip";
};

const joinClasses = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

const FEEDBACK_TOAST_AUTO_HIDE_MS = 5500;

const hasMappedVisualPolicy = (
  advancedVisualization: ReportGenerationWorkspaceModel["advancedVisualization"],
) => {
  const chartVisualization = advancedVisualization?.chartVisualization ?? null;
  return (chartVisualization?.chartOptions.length ?? 0) > 0;
};

type PackageDateFieldProps = {
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  className?: string;
};

const PackageDateField = ({
  value,
  onChange,
  className,
}: PackageDateFieldProps) => (
  <input type="date" value={value} onChange={onChange} className={className} />
);

const OPERATORS_BY_TYPE: Record<
  string,
  Array<{ value: string; label: string }>
> = {
  text: [
    { value: "equals", label: "equals" },
    { value: "contains", label: "contains" },
    { value: "starts_with", label: "starts with" },
  ],
  number: [
    { value: "equals", label: "=" },
    { value: "gt", label: ">" },
    { value: "lt", label: "<" },
    { value: "between", label: "between" },
  ],
  date: [
    { value: "before", label: "before" },
    { value: "after", label: "after" },
    { value: "between", label: "between" },
  ],
  enum: [
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
  ],
  boolean: [
    { value: "is_true", label: "is true" },
    { value: "is_false", label: "is false" },
  ],
};

const TABS = [
  { id: "templates", label: "Templates", icon: LayoutTemplate },
  { id: "filters", label: "Filters", icon: Filter },
  { id: "history", label: "History", icon: History },
] as const;

const MODULE_ORDER = [
  "Tuition",
  "Financial Aid",
  "Admissions",
  "Students & Families",
  "Cross-Module",
] as const;

const FILTER_ENTITY_ORDER = [
  "families",
  "students",
  "installments",
  "payments",
  "tuition_accounts",
  "ledger_entries",
  "contracts",
  "admissions_applications",
  "enrollment_records",
  "fa_applications",
  "scholarships",
  "notes",
  "documents",
] as const;

const FILTER_ENTITY_ORDER_INDEX = new Map<string, number>(
  FILTER_ENTITY_ORDER.map((entityId, index) => [entityId, index]),
);

type TemplateMenuActionKey =
  | "use"
  | "edit"
  | "schedule"
  | "visibility"
  | "version-history"
  | "duplicate"
  | "delete";
type HistoryMenuActionKey =
  | "use"
  | "rerun"
  | "schedule"
  | "version-history"
  | "download"
  | "duplicate"
  | "delete";

const templateMenuActions: Array<{
  key: TemplateMenuActionKey;
  label: string;
  icon: typeof Play;
  danger?: boolean;
}> = [
  { key: "use", label: "Use", icon: Play },
  { key: "edit", label: "Edit", icon: Pencil },
  { key: "schedule", label: "Schedule", icon: Calendar },
  { key: "visibility", label: "Share template", icon: Globe },
  { key: "version-history", label: "Version history", icon: History },
  { key: "duplicate", label: "Duplicate", icon: Copy },
  { key: "delete", label: "Delete", icon: Trash2, danger: true },
] as const;

const historyMenuActions: Array<{
  key: HistoryMenuActionKey;
  label: string;
  icon: typeof Play;
  danger?: boolean;
}> = [
  { key: "use", label: "Use", icon: Play },
  { key: "rerun", label: "Re-run", icon: RefreshCcw },
  { key: "schedule", label: "Schedule", icon: Calendar },
  { key: "version-history", label: "Version history", icon: History },
  { key: "download", label: "Download", icon: Download },
  { key: "duplicate", label: "Duplicate", icon: Copy },
  { key: "delete", label: "Delete", icon: Trash2, danger: true },
] as const;

const normalizeText = (value: string | null | undefined): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const formatPreviewCellValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toLocaleString() : "-";
  }
  return String(value);
};

const EXPORT_FORMAT_OPTIONS: Array<{
  format: ReportGenerationExportFormat;
  label: string;
  description: string;
}> = [
  {
    format: "xlsx",
    label: "Excel (.xlsx)",
    description: "Full spreadsheet with formatting",
  },
  {
    format: "csv",
    label: "CSV (.csv)",
    description: "Plain data, universal compatibility",
  },
  { format: "pdf", label: "PDF", description: "Printable document format" },
];

const escapeCsvCell = (value: unknown): string => {
  const text = formatPreviewCellValue(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const encodeCsvDataUrl = (rows: string[][]): string =>
  `data:text/csv;charset=utf-8,${encodeURIComponent(rows.map((row) => row.map(escapeCsvCell).join(",")).join("\n"))}`;

const safeReportFileStem = (value: string): string => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || "run-report";
};

const formatArtifactLabel = (fileName: string): string => {
  const normalized = fileName.toLowerCase();
  if (normalized.endsWith(".xlsx")) return "XLSX";
  if (normalized.endsWith(".csv")) return "CSV";
  if (normalized.endsWith(".pdf")) return "PDF";
  if (normalized.endsWith(".json")) return "JSON";
  if (normalized.endsWith(".md")) return "Markdown";
  return "Artifact";
};

export function ReportGenerationWorkspace({
  model,
  surfaceTheme = "light",
  insightPresentation = "cards",
}: ReportGenerationWorkspaceProps) {
  const [templateSearch, setTemplateSearch] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [openTemplateMenuId, setOpenTemplateMenuId] = useState<string | null>(
    null,
  );
  const [openHistoryMenuId, setOpenHistoryMenuId] = useState<string | null>(
    null,
  );
  const [templateDeleteConfirmId, setTemplateDeleteConfirmId] = useState<
    string | null
  >(null);
  const [hiddenHistoryEntryIds, setHiddenHistoryEntryIds] = useState<string[]>(
    [],
  );
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(
    null,
  );
  const [expandedFilterEntityIds, setExpandedFilterEntityIds] = useState<
    string[]
  >([]);
  const [selectedExportFormat, setSelectedExportFormat] =
    useState<ReportGenerationExportFormat>("xlsx");
  const [openingGoogleSheets, setOpeningGoogleSheets] = useState(false);
  const [googleSheetsError, setGoogleSheetsError] = useState<string | null>(
    null,
  );
  const [uncontrolledActiveBodySurface, setUncontrolledActiveBodySurface] =
    useState<ReportGenerationAdvancedVisualizationBodySurface>("table");
  const activeBodySurface = model.bodySurface ?? uncontrolledActiveBodySurface;
  const setActiveBodySurface = useCallback(
    (surface: ReportGenerationAdvancedVisualizationBodySurface) => {
      if (!model.actions.setBodySurface) {
        setUncontrolledActiveBodySurface(surface);
      }
      model.actions.setBodySurface?.(surface);
    },
    [model.actions],
  );
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [toolbarSaved, setToolbarSaved] = useState(true);
  const [dismissedFeedbackToastKey, setDismissedFeedbackToastKey] = useState<
    string | null
  >(null);
  const [showPreviewLoadingState, setShowPreviewLoadingState] = useState(false);
  const workspaceRootRef = useRef<HTMLDivElement | null>(null);
  const previewBodyRef = useRef<HTMLDivElement | null>(null);
  const [workspaceWidth, setWorkspaceWidth] = useState(0);
  const [previewLoadingOverlayStyle, setPreviewLoadingOverlayStyle] =
    useState<CSSProperties | null>(null);
  const isDarkSurfaceTheme = surfaceTheme === "dark";
  const rootClassName = isDarkSurfaceTheme
    ? "bg-[#071112] text-[#E8EFEF]"
    : "bg-[#F5F7F6] text-[#404F4D]";
  const panelClassName = isDarkSurfaceTheme
    ? "border-[#1B262B] bg-[#071112]"
    : "border-[#E5E7EB] bg-white";
  const panelTabsBorderClassName = isDarkSurfaceTheme
    ? "border-[#1B262B]"
    : "border-[#E5E7EB]";
  const panelTabsSurfaceClassName = isDarkSurfaceTheme
    ? "bg-[#071112]"
    : "bg-white";
  const activeTabClassName = isDarkSurfaceTheme
    ? "border-b-2 border-[#8BC0D2] bg-[#08171B] text-[#8BC0D2]"
    : "border-b-2 border-[#2697A2] bg-[#2697A2]/[0.03] text-[#2697A2]";
  const inactiveTabClassName = isDarkSurfaceTheme
    ? "text-[#91A2A6] hover:bg-[#0A1012] hover:text-[#E8EFEF]"
    : "text-[#6B7280] hover:bg-[#F4F6F3] hover:text-[#404F4D]";
  const collapseButtonClassName = isDarkSurfaceTheme
    ? "border-[#223137] bg-[#05090A] text-[#91A2A6] hover:bg-[#0B1214] hover:text-[#E8EFEF]"
    : "border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F8FAF9] hover:text-[#404F4D]";
  const previewHeaderClassName = isDarkSurfaceTheme
    ? "border-[#1B262B] bg-[#05090A]"
    : "border-[#E5E7EB] bg-white";
  const titleInputClassName = isDarkSurfaceTheme
    ? "text-[#E8EFEF]"
    : "text-[#404F4D]";
  const secondaryTextClassName = isDarkSurfaceTheme
    ? "text-[#91A2A6]"
    : "text-[#6B7280]";
  const tertiaryTextClassName = isDarkSurfaceTheme
    ? "text-[#69787C]"
    : "text-[#9CA3AF]";
  const feedbackPillClassName = isDarkSurfaceTheme
    ? "bg-[#0B1214] text-[#E0E8E8]"
    : "bg-[#F4F6F3] text-[#404F4D]";
  const toolbarSecondaryButtonClassName = isDarkSurfaceTheme
    ? "border-[#223137] bg-[#05090A] text-[#E0E8E8] hover:bg-[#0B1214]"
    : "border-[#E5E7EB] bg-white text-[#404F4D] hover:bg-[#F8FAF9]";
  const toolbarAccentButtonClassName = isDarkSurfaceTheme
    ? "border-[#1B4A4E] bg-[#0C2528] text-[#8FB7AE] hover:bg-[#123235]"
    : "border-[#CDEEE9] bg-[#E8F7F6] text-[#237062] hover:bg-[#DDF3EF]";
  const toolbarPrimaryButtonClassName = isDarkSurfaceTheme
    ? "bg-[#8BC0D2] text-[#071218] hover:bg-[#78B0C4]"
    : "bg-[#2697A2] text-white hover:bg-[#1D7D86]";
  const toolbarSavedButtonClassName = isDarkSurfaceTheme
    ? "border-[#1F403A] bg-[#0B1716] text-[#8FB7AE]"
    : "border-[#28E2A7]/30 bg-[#28E2A7]/[0.04] text-[#237062]";
  const bodySurfaceTabsClassName = isDarkSurfaceTheme
    ? "border-[#223137] bg-[#05090A]"
    : "border-[#E5E7EB] bg-white";
  const bodySurfaceTabActiveClassName = isDarkSurfaceTheme
    ? "bg-[#0C2528] text-[#8FB7AE] shadow-[0_1px_0_rgba(143,183,174,0.16)]"
    : "bg-[#E8F7F6] text-[#237062] shadow-sm";
  const bodySurfaceTabInactiveClassName = isDarkSurfaceTheme
    ? "text-[#91A2A6] hover:bg-[#0B1214] hover:text-[#E8EFEF]"
    : "text-[#6B7280] hover:bg-[#F8FAF9] hover:text-[#404F4D]";
  const feedbackToastClassName = isDarkSurfaceTheme
    ? "border-[#1B4A4E] bg-[#0B1214] text-[#E0E8E8] shadow-[0_18px_40px_rgba(0,0,0,0.38)]"
    : "border-[#CDEEE9] bg-white text-[#404F4D] shadow-[0_18px_36px_rgba(15,23,42,0.12)]";
  const feedbackErrorToastClassName = isDarkSurfaceTheme
    ? "border-[#5D2D2B] bg-[#251312] text-[#F0A29D] shadow-[0_18px_40px_rgba(0,0,0,0.38)]"
    : "border-[#CC4444]/20 bg-white text-[#CC4444] shadow-[0_18px_36px_rgba(15,23,42,0.12)]";
  const insightCardClassName = isDarkSurfaceTheme
    ? "border-[#1B262B] bg-[#060B0C] shadow-[0_18px_40px_rgba(0,0,0,0.34)]"
    : "border-[#E5E7EB] bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]";
  const insightStripClassName = isDarkSurfaceTheme
    ? "border-[#1B262B] bg-[#05090A] divide-[#1B262B]"
    : "border-[#E5E7EB] bg-white divide-[#E5E7EB]";
  const insightInfoBoxClassName = isDarkSurfaceTheme
    ? "text-[#91A2A6] hover:bg-[#0A1012]"
    : "text-[#6B7280] hover:bg-[#F8FAF9]";
  const insightDismissButtonClassName = isDarkSurfaceTheme
    ? "text-[#69787C] hover:bg-[#0B1214] hover:text-[#E8EFEF]"
    : "text-[#9CA3AF] hover:bg-[#F4F6F3] hover:text-[#404F4D]";
  const previewCardClassName = isDarkSurfaceTheme
    ? "border-[#1B262B] bg-[#060B0C] shadow-[0_22px_46px_rgba(0,0,0,0.34)]"
    : "border-[#E5E7EB] bg-white shadow-[0_14px_32px_rgba(15,23,42,0.06)]";
  const previewEyebrowClassName = isDarkSurfaceTheme
    ? "text-[#8FB7AE]"
    : "text-[#2697A2]";
  const previewChipClassName = isDarkSurfaceTheme
    ? "bg-[#0B1214] text-[#D8E2E3]"
    : "bg-[#F4F6F3] text-[#404F4D]";
  const previewStateAccentIconClassName = isDarkSurfaceTheme
    ? "bg-[#0C2528] text-[#8BC0D2]"
    : "bg-[#E8F7F6] text-[#2697A2]";
  const previewStateWarningIconClassName = isDarkSurfaceTheme
    ? "bg-[#26120F] text-[#E7A259]"
    : "bg-[#FFF4E5] text-[#C57A00]";
  const previewWarningBannerClassName = isDarkSurfaceTheme
    ? "border-[#524028] bg-[#20180D] text-[#E7C27A]"
    : "border-[#F7D7A4] bg-[#FFF9F0] text-[#C57A00]";
  const previewTableHeadBorderClassName = isDarkSurfaceTheme
    ? "border-[#1B262B]"
    : "border-[#E5E7EB]";
  const previewTableHeadCellClassName = isDarkSurfaceTheme
    ? "bg-[#0B1012] text-[#8EA0A5]"
    : "bg-[#F8FAF9] text-[#6B7280]";
  const previewCellTextClassName = isDarkSurfaceTheme
    ? "text-[#E8EFEF]"
    : "text-[#404F4D]";
  const previewRowBorderClassName = isDarkSurfaceTheme
    ? "border-[#111A1D]"
    : "border-[#F3F4F6]";
  const previewColumnMenuClassName = isDarkSurfaceTheme
    ? "border-[#1B262B] bg-[#060B0C] shadow-[0_18px_40px_rgba(0,0,0,0.34)]"
    : "border-[#E5E7EB] bg-white shadow-lg";
  const previewColumnMenuButtonClassName = isDarkSurfaceTheme
    ? "text-[#E8EFEF] hover:bg-[#0B1214]"
    : "text-[#404F4D] hover:bg-[#F4F6F3]";
  const exportMenuClassName = isDarkSurfaceTheme
    ? "border-[#1B262B] bg-[#060B0C] shadow-[0_22px_46px_rgba(0,0,0,0.34)]"
    : "border-[#E5E7EB] bg-white shadow-lg";
  const exportOptionClassName = isDarkSurfaceTheme
    ? "hover:border-[#1B4A4E] hover:bg-[#0B1214]"
    : "hover:border-[#CDEEE9] hover:bg-[#F8FFFE]";
  const exportOptionSelectedClassName = isDarkSurfaceTheme
    ? "border-[#1B4A4E] bg-[#0C2528]"
    : "border-[#2697A2] bg-[#F8FFFE]";
  const exportCancelButtonClassName = isDarkSurfaceTheme
    ? "border-[#223137] text-[#91A2A6] hover:bg-[#0B1214]"
    : "border-[#E5E7EB] text-[#6B7280] hover:bg-[#F8FAF9]";
  const runFullBundleButtonClassName = isDarkSurfaceTheme
    ? "border-[#223137] text-[#E0E8E8] hover:bg-[#0B1214]"
    : "border-[#E5E7EB] text-[#404F4D] hover:bg-[#F8FAF9]";
  const railSegmentedControlClassName = isDarkSurfaceTheme
    ? "bg-[#0B1214]"
    : "bg-[#F4F6F3]";
  const railSegmentedActiveClassName = isDarkSurfaceTheme
    ? "bg-[#071112] text-[#E8EFEF] shadow-[0_1px_0_rgba(143,183,174,0.18)]"
    : "bg-white text-[#404F4D] shadow-sm";
  const railSegmentedInactiveClassName = isDarkSurfaceTheme
    ? "text-[#91A2A6] hover:text-[#E8EFEF]"
    : "text-[#6B7280] hover:text-[#404F4D]";
  const railInputClassName = isDarkSurfaceTheme
    ? "border-[#223137] bg-[#071112] text-[#E8EFEF] placeholder:text-[#69787C] focus:border-[#8BC0D2] focus:ring-[#8BC0D2]/10"
    : "border-[#E5E7EB] bg-white text-[#404F4D] placeholder:text-[#9CA3AF] focus:border-[#2697A2] focus:ring-[#2697A2]/10";
  const templateCardClassName = isDarkSurfaceTheme
    ? "border-[#223137] bg-[#0B1214] text-[#E8EFEF] hover:border-[#1B4A4E] hover:bg-[#101B1E]"
    : "border-[#E5E7EB] bg-white text-[#404F4D] hover:border-[#CDEEE9] hover:bg-[#F8FFFE]";
  const templateActionIconButtonClassName = isDarkSurfaceTheme
    ? "text-[#91A2A6] hover:bg-[#101B1E] hover:text-[#E8EFEF]"
    : "text-[#9CA3AF] hover:bg-[#F4F6F3] hover:text-[#404F4D]";
  const templateActionMenuClassName = isDarkSurfaceTheme
    ? "border-[#223137] bg-[#060B0C] shadow-[0_18px_40px_rgba(0,0,0,0.34)]"
    : "border-[#E5E7EB] bg-white shadow-lg";
  const templateActionMenuButtonClassName = isDarkSurfaceTheme
    ? "text-[#E8EFEF] hover:bg-[#0B1214]"
    : "text-[#404F4D] hover:bg-[#F4F6F3]";
  const previewLoadingOverlayClassName = isDarkSurfaceTheme
    ? "bg-[#071112]/70 backdrop-blur-[2px]"
    : "bg-white/70 backdrop-blur-[2px]";

  useEffect(() => {
    const element = workspaceRootRef.current;
    if (!element) {
      return undefined;
    }
    const syncWidth = () => {
      setWorkspaceWidth(Math.round(element.getBoundingClientRect().width));
    };
    syncWidth();
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(syncWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const filterState = model.filterState.filterState;
  const isCompactWorkspaceLayout = workspaceWidth > 0 && workspaceWidth < 720;
  const runReportLoading = Boolean(model.runReportLoading);
  const previewSurfaceLoading = model.previewLoading || runReportLoading;
  const sidePanelStyle: CSSProperties = isCompactWorkspaceLayout
    ? {
        width: "100%",
        minWidth: "0",
        height: model.workspace.collapsed ? "48px" : "min(46vh, 360px)",
        minHeight: model.workspace.collapsed ? "48px" : "240px",
      }
    : {
        width: model.workspace.collapsed ? "48px" : "300px",
        minWidth: model.workspace.collapsed ? "48px" : "300px",
      };
  const tabsAreStacked = model.workspace.collapsed && !isCompactWorkspaceLayout;
  const hasRunnableSelection =
    filterState.selectedEntities.length > 0 &&
    filterState.selectedFields.length > 0;
  const runActionDisabled =
    model.isRunningReport || previewSurfaceLoading || !hasRunnableSelection;
  const exportActionDisabled =
    model.isRunningReport || !hasRunnableSelection || !model.canPreview;
  const reportGoogleSheetsArtifact = useMemo(() => {
    if (model.googleSheetsExportArtifact) {
      return model.googleSheetsExportArtifact;
    }
    if (
      !model.actions.openArtifactInGoogleSheets ||
      model.previewColumns.length === 0 ||
      model.previewRows.length === 0
    ) {
      return null;
    }
    const title =
      model.workspace.reportTitle.trim() ||
      model.resolvedTitle ||
      "Run Reports export";
    const fileName = `${safeReportFileStem(title)}.csv`;
    const rows = [
      model.previewColumns.map((column) => column.label),
      ...model.previewRows.map((row) =>
        model.previewColumns.map((column) =>
          formatPreviewCellValue(row.cells[column.fieldId]),
        ),
      ),
    ];
    const artifactDownloadUrl = encodeCsvDataUrl(rows);
    return {
      id: "run-reports-current-preview-google-sheets",
      label: `${title} Google Sheets`,
      kind: "spreadsheet",
      badge: "Google Sheets",
      meta: fileName,
      href: artifactDownloadUrl,
      sourceRef: "run-reports-current-preview",
      googleWorkspace: {
        kind: "google_sheets" as const,
        state: "exportable" as const,
        artifactDownloadUrl,
        mimeType: "text/csv",
        fileName,
        statusLabel:
          "Open the current Run Reports preview as a temporary Google Sheet.",
      },
    };
  }, [
    model.actions.openArtifactInGoogleSheets,
    model.googleSheetsExportArtifact,
    model.previewColumns,
    model.previewRows,
    model.resolvedTitle,
    model.workspace.reportTitle,
  ]);
  const googleSheetsAction = useMemo(
    () =>
      resolveAssistantGoogleSheetsExportAction(
        reportGoogleSheetsArtifact ? [reportGoogleSheetsArtifact] : [],
        model.actions.openArtifactInGoogleSheets,
      ),
    [model.actions.openArtifactInGoogleSheets, reportGoogleSheetsArtifact],
  );
  const activeFilterCount =
    filterState.conditions.length +
    (filterState.dateRange.preset !== "Custom" ||
    filterState.dateRange.from ||
    filterState.dateRange.to
      ? 1
      : 0);

  const activeSortField = useMemo(
    () =>
      model.availableFields.find(
        (field) => field.fieldId === filterState.sorts[0]?.fieldId,
      ) ?? null,
    [filterState.sorts, model.availableFields],
  );

  const galleryTemplates = useMemo(
    () =>
      MODULE_ORDER.map((moduleLabel) => ({
        moduleLabel,
        templates: model.galleryTemplates.filter(
          (template) => template.moduleLabel === moduleLabel,
        ),
      })).filter((group) => group.templates.length > 0),
    [model.galleryTemplates],
  );
  const showGalleryModuleHeaders = galleryTemplates.length > 1;

  const filteredMyTemplates = useMemo(
    () =>
      model.myTemplates.filter((template) => {
        if (!templateSearch.trim()) {
          return true;
        }
        const query = normalizeText(templateSearch);
        return (
          normalizeText(template.name).includes(query) ||
          normalizeText(template.description).includes(query)
        );
      }),
    [model.myTemplates, templateSearch],
  );

  const filteredHistoryEntries = useMemo(
    () =>
      model.historyEntries.filter((entry) => {
        if (hiddenHistoryEntryIds.includes(entry.id)) {
          return false;
        }
        if (!historySearch.trim()) {
          return true;
        }
        const query = normalizeText(historySearch);
        return (
          normalizeText(entry.templateName).includes(query) ||
          normalizeText(entry.format).includes(query) ||
          normalizeText(entry.status).includes(query)
        );
      }),
    [hiddenHistoryEntryIds, historySearch, model.historyEntries],
  );

  const moduleColorByLabel = useMemo(
    () =>
      new Map(
        model.modules.map((module) => [module.moduleLabel, module.color]),
      ),
    [model.modules],
  );

  const selectedVersionHistoryTemplate = useMemo(
    () =>
      model.myTemplates.find(
        (template) => template.id === model.workspace.versionHistoryTemplateId,
      ) ?? null,
    [model.myTemplates, model.workspace.versionHistoryTemplateId],
  );

  const filterEntities = useMemo(
    () =>
      model.modules
        .flatMap((module) => module.entities)
        .sort((left, right) => {
          const leftIndex =
            FILTER_ENTITY_ORDER_INDEX.get(left.id) ?? Number.MAX_SAFE_INTEGER;
          const rightIndex =
            FILTER_ENTITY_ORDER_INDEX.get(right.id) ?? Number.MAX_SAFE_INTEGER;
          if (leftIndex !== rightIndex) {
            return leftIndex - rightIndex;
          }
          return left.label.localeCompare(right.label);
        }),
    [model.modules],
  );

  const versionHistory = useMemo(
    () =>
      selectedVersionHistoryTemplate
        ? (model.versionHistoryByTemplateId[
            selectedVersionHistoryTemplate.id
          ] ?? [])
        : [],
    [model.versionHistoryByTemplateId, selectedVersionHistoryTemplate],
  );

  const openColumnMenuLabel = useMemo(
    () =>
      model.previewColumns.find(
        (column) => column.fieldId === model.workspace.contextMenuFieldId,
      )?.label ?? null,
    [model.previewColumns, model.workspace.contextMenuFieldId],
  );

  const runSelected = model.workspace.selectedHistoryEntryId
    ? (model.historyEntries.find(
        (entry) => entry.id === model.workspace.selectedHistoryEntryId,
      ) ?? null)
    : null;
  const lastActionIsError = Boolean(
    model.lastActionFeedback &&
      /^(Unable to|Report run was not accepted)/i.test(
        model.lastActionFeedback,
      ),
  );
  const feedbackMessage =
    model.lastActionFeedback === "Private template saved." ||
    model.lastActionFeedback === "Shared template saved."
      ? "Saved."
      : model.lastActionFeedback;
  const feedbackToastKey = feedbackMessage
    ? `${lastActionIsError ? "error" : "status"}:${feedbackMessage}`
    : null;
  const visibleFeedbackMessage =
    feedbackToastKey && dismissedFeedbackToastKey !== feedbackToastKey
      ? feedbackMessage
      : null;
  const previewToolbarVisible =
    model.canPreview || previewSurfaceLoading || Boolean(model.previewError);
  const hiddenColumnCount = model.workspace.hiddenColumns.length;
  const visualBodySurfaceAvailable = hasMappedVisualPolicy(
    model.advancedVisualization,
  );
  const visualPolicy =
    model.advancedVisualization?.chartVisualization?.policy ?? null;
  const visualPolicyDefaultBodySurface =
    visualPolicy?.defaultBodySurface ??
    (model.advancedVisualization?.chartVisualization?.chartOptions.length
      ? "visual"
      : "table");
  const visualPolicyUnmappedFallback =
    visualPolicy?.unmappedFallback ?? "table";
  const visualPolicyFallbackReason = visualPolicy?.fallbackReason ?? "";
  const visualPolicyCoverageSignature = visualPolicy?.coverageSignature ?? "";
  const visualPolicyDefaultChartOptionId =
    visualPolicy?.defaultChartOptionId ?? "";
  const visualPolicyGraphViewEnabled = visualPolicy?.graphViewEnabled === true;
  const activeBodySurfaceResolved: ReportGenerationAdvancedVisualizationBodySurface =
    activeBodySurface === "visual" && visualBodySurfaceAvailable
      ? "visual"
      : "table";
  const visualBodySurfaceActive = activeBodySurfaceResolved === "visual";
  const comparisonEntry = useMemo(() => {
    const selected = model.workspace.selectedHistoryEntryId
      ? (model.historyEntries.find(
          (entry) => entry.id === model.workspace.selectedHistoryEntryId,
        ) ?? null)
      : null;
    return (
      selected ??
      model.historyEntries.find(
        (entry) => !hiddenHistoryEntryIds.includes(entry.id),
      ) ??
      null
    );
  }, [
    hiddenHistoryEntryIds,
    model.historyEntries,
    model.workspace.selectedHistoryEntryId,
  ]);
  const comparisonRowDelta =
    comparisonEntry && typeof comparisonEntry.rowCount === "number"
      ? model.totalRowCount - comparisonEntry.rowCount
      : null;
  const comparisonColumnCount = model.previewColumns.length;

  useEffect(() => {
    if (!previewSurfaceLoading) {
      setShowPreviewLoadingState(false);
      return undefined;
    }
    setShowPreviewLoadingState(true);
    return undefined;
  }, [previewSurfaceLoading]);

  useEffect(() => {
    if (!showPreviewLoadingState) {
      setPreviewLoadingOverlayStyle(null);
      return undefined;
    }
    const element = previewBodyRef.current;
    if (!element || typeof window === "undefined") {
      return undefined;
    }

    let animationFrame = 0;
    const syncOverlayBounds = () => {
      const rect = element.getBoundingClientRect();
      const top = Math.max(rect.top, 0);
      const left = Math.max(rect.left, 0);
      const right = Math.min(rect.right, window.innerWidth);
      const bottom = Math.min(rect.bottom, window.innerHeight);
      setPreviewLoadingOverlayStyle({
        position: "fixed",
        top,
        left,
        width: Math.max(right - left, 0),
        height: Math.max(bottom - top, 0),
      });
    };
    const scheduleOverlayBoundsSync = () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      animationFrame = window.requestAnimationFrame(syncOverlayBounds);
    };

    syncOverlayBounds();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleOverlayBoundsSync)
        : null;
    resizeObserver?.observe(element);
    window.addEventListener("resize", scheduleOverlayBoundsSync);
    window.addEventListener("scroll", scheduleOverlayBoundsSync, true);

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleOverlayBoundsSync);
      window.removeEventListener("scroll", scheduleOverlayBoundsSync, true);
    };
  }, [showPreviewLoadingState]);

  useEffect(() => {
    if (!model.canPreview) {
      setToolbarSaved(true);
      return;
    }
    setToolbarSaved(false);
  }, [
    model.canPreview,
    model.resolvedTitle,
    model.workspace.hiddenColumns.length,
    model.workspace.sortColumn,
    model.workspace.sortDirection,
    model.totalRowCount,
  ]);

  useEffect(() => {
    if (feedbackMessage === "Saved.") {
      setToolbarSaved(true);
    }
  }, [feedbackMessage]);

  useEffect(() => {
    if (!feedbackToastKey) {
      setDismissedFeedbackToastKey(null);
      return undefined;
    }
    setDismissedFeedbackToastKey(null);
    if (lastActionIsError) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setDismissedFeedbackToastKey(feedbackToastKey);
    }, FEEDBACK_TOAST_AUTO_HIDE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [feedbackToastKey, lastActionIsError]);

  useEffect(() => {
    if (!model.canPreview) {
      setComparisonOpen(false);
    }
  }, [model.canPreview]);

  useEffect(() => {
    if (!visualBodySurfaceAvailable && activeBodySurface === "visual") {
      setActiveBodySurface("table");
    }
  }, [activeBodySurface, setActiveBodySurface, visualBodySurfaceAvailable]);

  useEffect(() => {
    setHiddenHistoryEntryIds((current) =>
      current.filter((entryId) =>
        model.historyEntries.some((entry) => entry.id === entryId),
      ),
    );
  }, [model.historyEntries]);

  const handleTemplateAction = (
    action: TemplateMenuActionKey,
    templateId: string,
  ) => {
    setOpenTemplateMenuId(null);
    switch (action) {
      case "use":
        model.actions.applyTemplateById(templateId, "custom");
        return;
      case "edit":
        model.actions.applyTemplateById(templateId, "custom");
        return;
      case "schedule":
        model.actions.toggleTemplateSchedule?.(templateId);
        return;
      case "visibility":
        model.actions.toggleTemplateVisibility?.(templateId);
        return;
      case "version-history":
        model.actions.openVersionHistory(templateId);
        return;
      case "duplicate":
        model.actions.duplicateTemplate?.(templateId);
        return;
      case "delete":
        setTemplateDeleteConfirmId(templateId);
        return;
      default:
        return;
    }
  };

  const handleHistoryAction = (
    action: HistoryMenuActionKey,
    entryId: string,
  ) => {
    setOpenHistoryMenuId(null);
    const entry = model.historyEntries.find(
      (candidate) => candidate.id === entryId,
    );
    if (!entry) {
      return;
    }
    switch (action) {
      case "use":
        model.actions.loadHistoryEntry(entryId);
        return;
      case "rerun":
        if (model.actions.rerunHistoryEntry) {
          model.actions.rerunHistoryEntry(entryId);
        } else {
          model.actions.loadHistoryEntry(entryId);
        }
        return;
      case "schedule":
        model.actions.scheduleHistoryEntry?.(entryId);
        return;
      case "version-history": {
        const versionTemplate =
          model.myTemplates.find(
            (template) => template.name === entry.templateName,
          ) ?? null;
        if (versionTemplate) {
          model.actions.openVersionHistory(versionTemplate.id);
        } else {
          model.actions.duplicateHistoryEntry?.(entryId);
        }
        return;
      }
      case "download":
        model.actions.downloadHistoryEntry?.(entryId);
        return;
      case "duplicate":
        model.actions.duplicateHistoryEntry?.(entryId);
        return;
      case "delete":
        setHiddenHistoryEntryIds((current) =>
          current.includes(entryId) ? current : [...current, entryId],
        );
        if (expandedHistoryId === entryId) {
          setExpandedHistoryId(null);
        }
        if (model.workspace.selectedHistoryEntryId === entryId) {
          model.actions.setSelectedHistoryEntry(null);
        }
        model.actions.deleteHistoryEntry?.(entryId);
        return;
      default:
        return;
    }
  };

  const handleToggleFilterEntity = (entityId: string, selected: boolean) => {
    model.actions.toggleEntity(entityId);
    setExpandedFilterEntityIds((current) => {
      if (selected) {
        return current.filter(
          (currentEntityId) => currentEntityId !== entityId,
        );
      }
      return current.includes(entityId) ? current : [...current, entityId];
    });
  };

  const handleToggleFilterEntityExpanded = (entityId: string) => {
    setExpandedFilterEntityIds((current) =>
      current.includes(entityId)
        ? current.filter((currentEntityId) => currentEntityId !== entityId)
        : [...current, entityId],
    );
  };

  const renderTemplateRailContent = () => {
    if (model.workspace.collapsed) {
      return null;
    }

    if (selectedVersionHistoryTemplate) {
      return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-4 pb-3">
            <button
              type="button"
              onClick={model.actions.closeVersionHistory}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-[#6B7280] transition-colors hover:bg-[#F4F6F3] hover:text-[#404F4D]"
              aria-label="Back to templates"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="min-w-0">
              <h3
                className="truncate text-base font-semibold text-[#404F4D]"
                style={{ fontFamily: "Mulish" }}
              >
                Version history
              </h3>
              <p className="truncate text-[11px] text-[#6B7280]">
                {selectedVersionHistoryTemplate.name}
              </p>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            <div className="relative pl-6">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#E5E7EB]" />
              <div className="space-y-4">
                {versionHistory.map((entry, index) => (
                  <div
                    key={`${selectedVersionHistoryTemplate.id}-${entry.version}`}
                    className="relative flex gap-3"
                  >
                    <div
                      className={`z-10 mt-0.5 h-[15px] w-[15px] rounded-full border-2 ${index === 0 ? "border-[#2697A2] bg-[#2697A2]" : "border-[#CBD5E1] bg-white"}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#404F4D]">
                          v{entry.version}
                        </span>
                        {index === 0 ? (
                          <span className="text-[11px] font-medium text-[#2697A2]">
                            Current
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-0.5 text-[11px] text-[#6B7280]">
                        {entry.date}
                      </p>
                      <p className="mt-1 text-sm text-[#4B5563]">
                        {entry.summary}
                      </p>
                      {index > 0 ? (
                        <button
                          type="button"
                          onClick={() => {
                            model.actions.applyTemplateById(
                              selectedVersionHistoryTemplate.id,
                              "custom",
                            );
                            model.actions.closeVersionHistory();
                          }}
                          className="mt-1.5 text-[11px] font-semibold text-[#2697A2] transition-colors hover:text-[#1D7D86]"
                        >
                          Revert to this version
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <h2
            className={joinClasses(
              "text-lg font-semibold",
              titleInputClassName,
            )}
            style={{ fontFamily: "Mulish" }}
          >
            Templates
          </h2>
          <button
            type="button"
            onClick={model.actions.startNewReport}
            className={joinClasses(
              "h-8 rounded-sm px-3 text-sm font-medium transition-colors",
              toolbarPrimaryButtonClassName,
            )}
          >
            New report
          </button>
        </div>

        <div className="px-4 pb-3">
          <div
            className={joinClasses(
              "flex rounded-md p-1",
              railSegmentedControlClassName,
            )}
          >
            {(["gallery", "my"] as const).map((view) => (
              <button
                key={view}
                type="button"
                onClick={() => model.actions.setTemplateView(view)}
                className={joinClasses(
                  "flex-1 rounded-sm px-3 py-1.5 text-left text-sm font-medium transition-colors",
                  model.workspace.templateView === view
                    ? railSegmentedActiveClassName
                    : railSegmentedInactiveClassName,
                )}
              >
                {view === "gallery" ? "Gallery" : "My templates"}
              </button>
            ))}
          </div>
        </div>

        {model.workspace.templateView === "my" ? (
          <div className="px-4 pb-3">
            <label className="relative block">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
              />
              <input
                value={templateSearch}
                onChange={(event) => setTemplateSearch(event.target.value)}
                placeholder="Search templates..."
                className={joinClasses(
                  "h-9 w-full rounded-sm border pl-9 pr-3 text-sm outline-none transition-colors focus:ring-2",
                  railInputClassName,
                )}
              />
            </label>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          {model.workspace.templateView === "gallery" ? (
            <div className="space-y-5">
              {galleryTemplates.map((group) => (
                <section key={group.moduleLabel}>
                  {showGalleryModuleHeaders ? (
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{
                          backgroundColor:
                            moduleColorByLabel.get(group.moduleLabel) ??
                            "#9CA3AF",
                        }}
                      />
                      <h3
                        className={joinClasses(
                          "text-[11px] font-semibold uppercase tracking-[0.16em]",
                          secondaryTextClassName,
                        )}
                      >
                        {group.moduleLabel}
                      </h3>
                    </div>
                  ) : null}
                  <div className="space-y-2">
                    {group.templates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() =>
                          model.actions.applyTemplateById(
                            template.id,
                            "gallery",
                          )
                        }
                        className={joinClasses(
                          "w-full rounded-lg border px-3 py-3 text-left transition-colors",
                          templateCardClassName,
                        )}
                        data-testid="report-generation-gallery-template-card"
                        data-template-id={template.id}
                        data-template-name={template.name}
                        data-template-module={template.moduleLabel}
                      >
                        <p
                          className={joinClasses(
                            "text-sm font-semibold",
                            titleInputClassName,
                          )}
                        >
                          {template.name}
                        </p>
                        <p
                          className={joinClasses(
                            "mt-1 text-xs leading-5",
                            secondaryTextClassName,
                          )}
                        >
                          {template.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMyTemplates.length === 0 ? (
                <p className="py-6 text-center text-sm text-[#9CA3AF]">
                  No templates found
                </p>
              ) : (
                filteredMyTemplates.map((template) => (
                  <div
                    key={template.id}
                    className={joinClasses(
                      "rounded-lg border px-3 py-3",
                      templateCardClassName,
                    )}
                    data-testid="report-generation-my-template-card-row"
                  >
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          model.actions.applyTemplateById(template.id, "custom")
                        }
                        className="min-w-0 flex-1 text-left"
                        data-testid="report-generation-my-template-card"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <p
                            className={joinClasses(
                              "truncate text-sm font-semibold",
                              titleInputClassName,
                            )}
                          >
                            {template.name}
                          </p>
                          {template.isScheduled ? (
                            <Calendar
                              size={13}
                              className="shrink-0 text-[#2697A2]"
                            />
                          ) : null}
                        </div>
                        <p
                          className={joinClasses(
                            "mt-1 text-xs leading-5",
                            secondaryTextClassName,
                          )}
                        >
                          {template.description}
                        </p>
                        <div
                          className={joinClasses(
                            "mt-2 flex flex-wrap items-center gap-2 text-[11px]",
                            secondaryTextClassName,
                          )}
                        >
                          <span className="inline-flex items-center gap-1">
                            {template.visibility === "Shared" ? (
                              <Globe size={12} />
                            ) : (
                              <Lock size={12} />
                            )}
                            {template.visibility}
                          </span>
                          <span>v{template.version}</span>
                          <span>{template.lastModified}</span>
                        </div>
                      </button>
                      <div className="relative shrink-0">
                        <button
                          type="button"
                          onClick={() =>
                            setOpenTemplateMenuId((current) =>
                              current === template.id ? null : template.id,
                            )
                          }
                          className={joinClasses(
                            "flex h-7 w-7 items-center justify-center rounded-sm transition-colors",
                            templateActionIconButtonClassName,
                          )}
                          aria-label={`Template actions for ${template.name}`}
                        >
                          <MoreVertical size={16} />
                        </button>
                        {openTemplateMenuId === template.id ? (
                          <div
                            className={joinClasses(
                              "absolute right-0 top-8 z-20 w-44 rounded-lg border py-1",
                              templateActionMenuClassName,
                            )}
                          >
                            {templateMenuActions.map((action) => {
                              const Icon = action.icon;
                              const actionLabel =
                                action.key === "schedule" &&
                                template.isScheduled
                                  ? "Remove schedule"
                                  : action.key === "visibility"
                                    ? template.visibility === "Shared"
                                      ? "Make private"
                                      : "Share template"
                                    : action.label;
                              const actionAvailable =
                                action.key === "use" ||
                                action.key === "edit" ||
                                action.key === "version-history" ||
                                (action.key === "schedule" &&
                                  Boolean(
                                    model.actions.toggleTemplateSchedule,
                                  )) ||
                                (action.key === "visibility" &&
                                  Boolean(
                                    model.actions.toggleTemplateVisibility,
                                  )) ||
                                (action.key === "duplicate" &&
                                  Boolean(model.actions.duplicateTemplate)) ||
                                (action.key === "delete" &&
                                  Boolean(model.actions.deleteTemplate));
                              if (!actionAvailable) {
                                return null;
                              }
                              return (
                                <button
                                  key={action.key}
                                  type="button"
                                  onClick={() =>
                                    handleTemplateAction(
                                      action.key,
                                      template.id,
                                    )
                                  }
                                  className={joinClasses(
                                    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                                    action.danger
                                      ? joinClasses(
                                          "text-[#CC4444]",
                                          isDarkSurfaceTheme
                                            ? "hover:bg-[#2A1414]"
                                            : "hover:bg-[#FFF1F1]",
                                        )
                                      : templateActionMenuButtonClassName,
                                  )}
                                >
                                  <Icon size={14} />
                                  <span className="flex-1">{actionLabel}</span>
                                  {action.key === "schedule" &&
                                  template.isScheduled ? (
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#2697A2]" />
                                  ) : null}
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {templateDeleteConfirmId === template.id ? (
                      <div
                        className={joinClasses(
                          "mt-3 rounded-lg border px-3 py-3",
                          isDarkSurfaceTheme
                            ? "border-[#5D2D2B] bg-[#251312]"
                            : "border-[#CC4444]/20 bg-[#FFF5F5]",
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <AlertTriangle
                            size={15}
                            className="mt-0.5 shrink-0 text-[#CC4444]"
                          />
                          <div className="min-w-0 flex-1">
                            <p
                              className={joinClasses(
                                "text-sm font-semibold",
                                titleInputClassName,
                              )}
                            >
                              Delete "{template.name}"?
                            </p>
                            <p
                              className={joinClasses(
                                "mt-0.5 text-xs leading-5",
                                secondaryTextClassName,
                              )}
                            >
                              {template.isScheduled
                                ? "This will also cancel the active schedule."
                                : "This action cannot be undone."}
                            </p>
                            <div className="mt-2 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setTemplateDeleteConfirmId(null);
                                  model.actions.deleteTemplate?.(template.id);
                                }}
                                className="h-7 rounded-sm bg-[#CC4444] px-3 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                                data-testid="report-generation-confirm-template-delete-button"
                              >
                                Delete
                              </button>
                              <button
                                type="button"
                                onClick={() => setTemplateDeleteConfirmId(null)}
                                className={joinClasses(
                                  "h-7 rounded-sm border px-3 text-xs font-medium transition-colors",
                                  exportCancelButtonClassName,
                                )}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFiltersContent = () => {
    if (model.workspace.collapsed) {
      return null;
    }

    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <h2
            className={joinClasses(
              "text-lg font-semibold",
              titleInputClassName,
            )}
            style={{ fontFamily: "Mulish" }}
          >
            Filters
          </h2>
          {filterState.selectedEntities.length > 0 || activeFilterCount > 0 ? (
            <button
              type="button"
              onClick={model.actions.clearAll}
              className={joinClasses(
                "text-sm font-medium transition-colors hover:text-[#CC4444]",
                secondaryTextClassName,
              )}
            >
              Clear all
            </button>
          ) : null}
        </div>

        <div className="px-4 pb-3">
          <label className="relative block">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
            />
            <input
              value={model.filterState.entitySearch}
              onChange={(event) =>
                model.actions.setEntitySearch(event.target.value)
              }
              className={joinClasses(
                "h-9 w-full rounded-sm border pl-9 pr-3 text-sm outline-none transition-colors focus:ring-2",
                railInputClassName,
              )}
              placeholder="Search entities and fields..."
            />
          </label>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-4">
          <section>
            <h3
              className={joinClasses(
                "mb-2 text-xs font-medium",
                secondaryTextClassName,
              )}
            >
              Entities
            </h3>
            <div className="space-y-1">
              {filterEntities.map((entity) => {
                const isExpanded = expandedFilterEntityIds.includes(entity.id);
                return (
                  <div key={entity.id}>
                    <div
                      className={joinClasses(
                        "flex h-8 items-center gap-2 rounded-sm px-2 transition-colors",
                        entity.selected
                          ? isDarkSurfaceTheme
                            ? "bg-[#0C2528]"
                            : "bg-[#2697A2]/5"
                          : isDarkSurfaceTheme
                            ? "hover:bg-[#101B1E]"
                            : "hover:bg-[#F4F6F3]",
                      )}
                      data-testid="report-generation-entity-row"
                      data-entity-id={entity.id}
                    >
                      <input
                        type="checkbox"
                        checked={entity.selected}
                        onChange={() => {
                          model.actions.toggleEntity(entity.id);
                          setExpandedFilterEntityIds((current) => {
                            if (entity.selected) {
                              return current.filter(
                                (currentEntityId) =>
                                  currentEntityId !== entity.id,
                              );
                            }
                            return current.includes(entity.id)
                              ? current
                              : [...current, entity.id];
                          });
                        }}
                        aria-label={`Select ${entity.label}`}
                        className="h-4 w-4 rounded-sm border-[#6B7280] accent-[#2697A2] text-[#2697A2] focus:ring-[#2697A2]/20 focus:ring-offset-0"
                        data-testid="report-generation-entity-checkbox"
                        data-entity-id={entity.id}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (entity.selected) {
                            handleToggleFilterEntityExpanded(entity.id);
                          } else {
                            handleToggleFilterEntity(entity.id, false);
                          }
                        }}
                        className={joinClasses(
                          "flex min-w-0 flex-1 items-center gap-1.5 text-left text-sm font-medium",
                          titleInputClassName,
                        )}
                        data-testid="report-generation-entity-toggle"
                        data-entity-id={entity.id}
                      >
                        {entity.selected ? (
                          isExpanded ? (
                            <ChevronDown
                              size={14}
                              className="shrink-0 text-[#9CA3AF]"
                            />
                          ) : (
                            <ChevronRight
                              size={14}
                              className="shrink-0 text-[#9CA3AF]"
                            />
                          )
                        ) : (
                          <span className="h-3.5 w-3.5 shrink-0" />
                        )}
                        <span className="truncate">{entity.label}</span>
                      </button>
                      <span
                        className={joinClasses(
                          "max-w-[104px] truncate text-[11px]",
                          tertiaryTextClassName,
                        )}
                      >
                        {entity.moduleLabel}
                      </span>
                    </div>

                    {entity.selected && isExpanded ? (
                      <div className="ml-6 mt-1 mb-2 space-y-2">
                        {entity.visibleCategories.map((category) => (
                          <div key={category.name}>
                            <p
                              className={joinClasses(
                                "mb-1 text-[11px] font-medium",
                                tertiaryTextClassName,
                              )}
                            >
                              {category.name}
                            </p>
                            <div className="space-y-0.5">
                              {category.fields.map((field) => (
                                <label
                                  key={field.id}
                                  className={joinClasses(
                                    "flex h-7 items-center gap-2 rounded-sm px-2 text-sm transition-colors",
                                    field.locked
                                      ? "cursor-not-allowed opacity-70"
                                      : "cursor-pointer",
                                    titleInputClassName,
                                    !field.locked &&
                                      (isDarkSurfaceTheme
                                        ? "hover:bg-[#101B1E]"
                                        : "hover:bg-[#F4F6F3]"),
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    checked={field.selected}
                                    disabled={field.locked}
                                    onChange={() =>
                                      model.actions.toggleField(field.id)
                                    }
                                    aria-label={`Select ${field.label}`}
                                    className="h-3.5 w-3.5 rounded-sm border-[#6B7280] accent-[#2697A2] text-[#2697A2] focus:ring-[#2697A2]/20 focus:ring-offset-0"
                                    data-testid="report-generation-field-checkbox"
                                    data-entity-id={entity.id}
                                    data-field-id={field.id}
                                  />
                                  <span className="min-w-0 flex-1 truncate">
                                    {field.label}
                                  </span>
                                  <span
                                    className={joinClasses(
                                      "text-[11px]",
                                      tertiaryTextClassName,
                                    )}
                                  >
                                    {field.type}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>

          {model.joins.length > 0 ? (
            <div className="space-y-1">
              {model.joins.map((join) => (
                <div
                  key={join}
                  className="flex items-center gap-2 rounded-md border border-[#2697A2]/15 bg-[#2697A2]/5 px-3 py-2 text-[11px] font-semibold text-[#2697A2]"
                >
                  <Link size={14} className="shrink-0" />
                  <span>{join}</span>
                </div>
              ))}
            </div>
          ) : null}

          <section>
            <h3
              className={joinClasses(
                "mb-2 text-xs font-medium",
                secondaryTextClassName,
              )}
            >
              Date range
            </h3>
            <div className="space-y-2">
              <select
                value={filterState.dateRange.preset}
                onChange={(event) =>
                  model.actions.updateDateRange({ preset: event.target.value })
                }
                className={joinClasses(
                  "h-9 w-full rounded-sm border px-3 text-sm outline-none transition-colors focus:ring-2",
                  railInputClassName,
                )}
              >
                {[
                  "Custom",
                  "This Month",
                  "Last Month",
                  "This Quarter",
                  "This Year",
                  "Last Year",
                  "Year-to-Date",
                ].map((preset) => (
                  <option key={preset} value={preset}>
                    {preset}
                  </option>
                ))}
              </select>
              {filterState.dateRange.preset === "Custom" ? (
                <div className="grid grid-cols-2 gap-2">
                  <label className="relative block">
                    <Calendar
                      size={14}
                      className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
                    />
                    <PackageDateField
                      value={filterState.dateRange.from}
                      onChange={(event) =>
                        model.actions.updateDateRange({
                          from: event.target.value,
                        })
                      }
                      className={joinClasses(
                        "h-9 w-full rounded-sm border pl-8 pr-2 text-xs outline-none transition-colors",
                        railInputClassName,
                      )}
                    />
                  </label>
                  <label className="relative block">
                    <Calendar
                      size={14}
                      className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
                    />
                    <PackageDateField
                      value={filterState.dateRange.to}
                      onChange={(event) =>
                        model.actions.updateDateRange({
                          to: event.target.value,
                        })
                      }
                      className={joinClasses(
                        "h-9 w-full rounded-sm border pl-8 pr-2 text-xs outline-none transition-colors",
                        railInputClassName,
                      )}
                    />
                  </label>
                </div>
              ) : null}
            </div>
          </section>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3
                className={joinClasses(
                  "text-xs font-medium",
                  secondaryTextClassName,
                )}
              >
                Conditions
              </h3>
              <button
                type="button"
                onClick={() =>
                  model.actions.addCondition(model.availableFields[0] ?? null)
                }
                disabled={filterState.selectedEntities.length === 0}
                className="flex items-center gap-1 text-[11px] font-semibold text-[#2697A2] transition-colors hover:text-[#1D7D86] disabled:cursor-not-allowed disabled:text-[#9CA3AF]"
              >
                <Plus size={14} />
                Add
              </button>
            </div>
            {filterState.conditions.length === 0 ? (
              <p className={joinClasses("py-2 text-sm", tertiaryTextClassName)}>
                {filterState.selectedEntities.length === 0
                  ? "Select entities to add conditions"
                  : "No conditions - showing all records"}
              </p>
            ) : (
              <div className="space-y-2">
                {filterState.conditions.map((condition) => {
                  const operatorOptions =
                    OPERATORS_BY_TYPE[condition.fieldType] ??
                    OPERATORS_BY_TYPE.text;
                  return (
                    <div
                      key={condition.id}
                      className={joinClasses(
                        "space-y-1.5 rounded-md border p-2",
                        templateCardClassName,
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        <select
                          value={condition.fieldId}
                          onChange={(event) => {
                            const nextField =
                              model.availableFields.find(
                                (field) => field.fieldId === event.target.value,
                              ) ?? null;
                            if (!nextField) {
                              return;
                            }
                            model.actions.updateCondition(condition.id, {
                              fieldId: nextField.fieldId,
                              fieldLabel: nextField.fieldLabel,
                              fieldType: nextField.fieldType,
                              operator:
                                OPERATORS_BY_TYPE[nextField.fieldType]?.[0]
                                  ?.value ?? "equals",
                              value: "",
                            });
                          }}
                          className={joinClasses(
                            "h-7 min-w-0 flex-1 rounded-sm border px-2 text-[11px] outline-none",
                            railInputClassName,
                          )}
                        >
                          {model.availableFields.map((field) => (
                            <option key={field.fieldId} value={field.fieldId}>
                              {field.fieldLabel}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() =>
                            model.actions.removeCondition(condition.id)
                          }
                          className={joinClasses(
                            "flex h-6 w-6 items-center justify-center rounded-sm transition-colors hover:text-[#CC4444]",
                            tertiaryTextClassName,
                            isDarkSurfaceTheme
                              ? "hover:bg-[#2A1414]"
                              : "hover:bg-[#FFF5F6]",
                          )}
                          aria-label="Remove condition"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <select
                          value={condition.operator}
                          onChange={(event) =>
                            model.actions.updateCondition(condition.id, {
                              operator: event.target.value,
                            })
                          }
                          className={joinClasses(
                            "h-7 w-24 rounded-sm border px-2 text-[11px] outline-none",
                            railInputClassName,
                          )}
                        >
                          {operatorOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <input
                          value={condition.value}
                          onChange={(event) =>
                            model.actions.updateCondition(condition.id, {
                              value: event.target.value,
                            })
                          }
                          className={joinClasses(
                            "h-7 min-w-0 flex-1 rounded-sm border px-2 text-[11px] outline-none",
                            railInputClassName,
                          )}
                          placeholder="Value..."
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section>
            <h3
              className={joinClasses(
                "mb-2 text-xs font-medium",
                secondaryTextClassName,
              )}
            >
              Sort
            </h3>
            {filterState.selectedFields.length === 0 ? (
              <p className={joinClasses("py-2 text-sm", tertiaryTextClassName)}>
                Select fields to configure sort
              </p>
            ) : (
              <div className="flex items-center gap-2">
                <select
                  value={activeSortField?.fieldId ?? ""}
                  onChange={(event) => {
                    const nextField =
                      model.availableFields.find(
                        (field) => field.fieldId === event.target.value,
                      ) ?? null;
                    model.actions.setPrimarySortField(nextField);
                  }}
                  className={joinClasses(
                    "h-9 min-w-0 flex-1 rounded-sm border px-3 text-sm outline-none transition-colors focus:ring-2",
                    railInputClassName,
                  )}
                >
                  <option value="">No sort</option>
                  {model.availableFields
                    .filter((field) =>
                      filterState.selectedFields.includes(field.fieldId),
                    )
                    .map((field) => (
                      <option key={field.fieldId} value={field.fieldId}>
                        {field.fieldLabel}
                      </option>
                    ))}
                </select>
                {filterState.sorts.length > 0 ? (
                  <button
                    type="button"
                    onClick={model.actions.togglePrimarySortDirection}
                    className={joinClasses(
                      "flex h-9 w-9 items-center justify-center rounded-sm border transition-colors",
                      collapseButtonClassName,
                    )}
                    aria-label={
                      filterState.sorts[0]?.direction === "asc"
                        ? "Sort ascending"
                        : "Sort descending"
                    }
                  >
                    {filterState.sorts[0]?.direction === "desc" ? (
                      <ArrowDown size={16} />
                    ) : (
                      <ArrowUp size={16} />
                    )}
                  </button>
                ) : null}
              </div>
            )}
          </section>
        </div>

        {hasRunnableSelection ? (
          <div
            className={joinClasses(
              "border-t px-4 py-3",
              panelTabsBorderClassName,
            )}
          >
            <button
              type="button"
              onClick={model.actions.runReport}
              disabled={runActionDisabled}
              className={joinClasses(
                "h-10 w-full rounded-sm text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                toolbarPrimaryButtonClassName,
              )}
              data-testid="report-generation-run-report-button"
            >
              {model.isRunningReport || runReportLoading
                ? "Running..."
                : "Run report"}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderHistoryContent = () => {
    if (model.workspace.collapsed) {
      return null;
    }

    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <div className="px-4 pt-4 pb-3">
          <h2
            className={joinClasses(
              "text-lg font-semibold",
              titleInputClassName,
            )}
            style={{ fontFamily: "Mulish" }}
          >
            Run history
          </h2>
        </div>
        <div className="px-4 pb-3">
          <label className="relative block">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
            />
            <input
              value={historySearch}
              onChange={(event) => setHistorySearch(event.target.value)}
              placeholder="Search history..."
              className={joinClasses(
                "h-9 w-full rounded-sm border pl-9 pr-3 text-sm outline-none transition-colors focus:ring-2",
                railInputClassName,
              )}
            />
          </label>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pl-4 pr-5 pb-4">
          <div className="space-y-2">
            {filteredHistoryEntries.length === 0 ? (
              <p
                className={joinClasses(
                  "py-6 text-center text-sm",
                  tertiaryTextClassName,
                )}
              >
                No matching history
              </p>
            ) : (
              filteredHistoryEntries.map((entry) => {
                const isExpanded = expandedHistoryId === entry.id;
                const isSelected =
                  model.workspace.selectedHistoryEntryId === entry.id;
                const hasTemplateVersionHistory = model.myTemplates.some(
                  (template) =>
                    template.id === entry.templateId ||
                    template.name === entry.templateName,
                );
                return (
                  <div
                    key={entry.id}
                    className={joinClasses(
                      "min-w-0 rounded-lg border transition-shadow hover:shadow-sm",
                      templateCardClassName,
                      isSelected ? "border-[#2697A2]/40" : "",
                    )}
                    data-testid="report-generation-history-entry"
                  >
                    <div className="flex items-start gap-3 p-3">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedHistoryId((current) =>
                            current === entry.id ? null : entry.id,
                          )
                        }
                        className="mt-0.5 shrink-0 text-[#9CA3AF]"
                        aria-label={
                          isExpanded
                            ? "Collapse history entry"
                            : "Expand history entry"
                        }
                      >
                        {isExpanded ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => model.actions.loadHistoryEntry(entry.id)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <p
                          className={joinClasses(
                            "line-clamp-2 break-words text-sm font-semibold",
                            titleInputClassName,
                          )}
                          title={entry.templateName}
                        >
                          {entry.templateName}
                        </p>
                        <div
                          className={joinClasses(
                            "mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px]",
                            secondaryTextClassName,
                          )}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className={joinClasses(
                                "h-1.5 w-1.5 rounded-full",
                                entry.status === "Completed" ||
                                  entry.status === "Scheduled"
                                  ? "bg-[#2697A2]"
                                  : entry.status === "Failed" ||
                                      entry.status === "Cancelled"
                                    ? "bg-[#CC4444]"
                                    : "bg-[#C57A00]",
                              )}
                            />
                            {entry.status}
                          </span>
                          <span>•</span>
                          <span>{entry.format}</span>
                          <span>•</span>
                          <span>{entry.runDate}</span>
                        </div>
                      </button>
                      <div className="relative shrink-0">
                        <button
                          type="button"
                          onClick={() =>
                            setOpenHistoryMenuId((current) =>
                              current === entry.id ? null : entry.id,
                            )
                          }
                          className={joinClasses(
                            "flex h-7 w-7 items-center justify-center rounded-sm transition-colors",
                            templateActionIconButtonClassName,
                          )}
                          aria-label={`History actions for ${entry.templateName}`}
                        >
                          <MoreVertical size={16} />
                        </button>
                        {openHistoryMenuId === entry.id ? (
                          <div
                            className={joinClasses(
                              "absolute right-0 top-8 z-20 w-44 rounded-lg border py-1",
                              templateActionMenuClassName,
                            )}
                          >
                            {historyMenuActions.map((action) => {
                              const Icon = action.icon;
                              const actionAvailable =
                                action.key === "use" ||
                                action.key === "rerun" ||
                                (action.key === "schedule" &&
                                  Boolean(
                                    model.actions.scheduleHistoryEntry,
                                  )) ||
                                (action.key === "version-history" &&
                                  (hasTemplateVersionHistory ||
                                    Boolean(
                                      model.actions.duplicateHistoryEntry,
                                    ))) ||
                                (action.key === "download" &&
                                  entry.artifactRefs.length > 0 &&
                                  Boolean(
                                    model.actions.downloadHistoryEntry,
                                  )) ||
                                (action.key === "duplicate" &&
                                  Boolean(
                                    model.actions.duplicateHistoryEntry,
                                  )) ||
                                action.key === "delete";
                              if (!actionAvailable) {
                                return null;
                              }
                              const actionLabel =
                                action.key === "version-history" &&
                                !hasTemplateVersionHistory
                                  ? "Save version copy"
                                  : action.label;
                              return (
                                <button
                                  key={action.key}
                                  type="button"
                                  onClick={() =>
                                    handleHistoryAction(action.key, entry.id)
                                  }
                                  className={joinClasses(
                                    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                                    action.danger
                                      ? joinClasses(
                                          "text-[#CC4444]",
                                          isDarkSurfaceTheme
                                            ? "hover:bg-[#2A1414]"
                                            : "hover:bg-[#FFF1F1]",
                                        )
                                      : templateActionMenuButtonClassName,
                                  )}
                                >
                                  <Icon size={14} />
                                  <span>{actionLabel}</span>
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {isExpanded ? (
                      <div
                        className={joinClasses(
                          "border-t px-4 py-3 text-xs",
                          panelTabsBorderClassName,
                          secondaryTextClassName,
                        )}
                      >
                        <div className="grid min-w-0 gap-2">
                          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
                            <span>Rows</span>
                            <span className="min-w-0 break-words text-right">
                              {typeof entry.rowCount === "number"
                                ? entry.rowCount.toLocaleString()
                                : "Pending"}
                            </span>
                          </div>
                          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
                            <span>Saved run</span>
                            <span className="min-w-0 break-words text-right">{entry.runDate}</span>
                          </div>
                          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3">
                            <span>Output</span>
                            <span className="min-w-0 break-words text-right">{entry.format}</span>
                          </div>
                          {entry.statusMessage ? (
                            <div
                              className={joinClasses(
                                "break-words rounded-md px-3 py-2 leading-5",
                                feedbackPillClassName,
                              )}
                            >
                              {entry.statusMessage}
                            </div>
                          ) : null}
                          <div className="pt-1">
                            <p
                              className={joinClasses(
                                "mb-1 text-[11px] font-semibold uppercase tracking-[0.12em]",
                                tertiaryTextClassName,
                              )}
                            >
                              Downloads
                            </p>
                            {entry.artifactRefs.length > 0 ? (
                              <div className="grid gap-1.5">
                                {entry.artifactRefs.map((artifact) => (
                                  <a
                                    key={`${artifact.id}-${artifact.downloadUrl}`}
                                    href={artifact.downloadUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    download={resolveAssistantArtifactDownloadFileName(
                                      artifact,
                                    )}
                                    className={joinClasses(
                                      "flex min-w-0 items-center justify-between gap-3 overflow-hidden rounded-md border px-3 py-2 font-medium transition-colors",
                                      toolbarAccentButtonClassName,
                                    )}
                                    aria-label={`Download ${artifact.label || artifact.fileName}`}
                                    title={artifact.label || artifact.fileName}
                                    data-testid="report-generation-history-artifact-link"
                                  >
                                    <span className="min-w-0 flex-1 break-words leading-4">
                                      {artifact.label || artifact.fileName}
                                    </span>
                                    <span
                                      className={joinClasses(
                                        "max-w-16 shrink-0 truncate text-right text-[10px] uppercase tracking-[0.12em]",
                                        secondaryTextClassName,
                                      )}
                                      title={formatArtifactLabel(artifact.fileName)}
                                    >
                                      {formatArtifactLabel(artifact.fileName)}
                                    </span>
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <p
                                className={joinClasses(
                                  "rounded-md px-3 py-2",
                                  feedbackPillClassName,
                                )}
                              >
                                Downloads will appear here when the run
                                finishes.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleToolbarSave = () => {
    model.actions.savePrivateTemplate();
  };

  const handlePrintReport = () => {
    if (model.actions.printReport) {
      model.actions.printReport();
      return;
    }
    model.actions.closeExport();
    if (typeof window !== "undefined") {
      window.print();
    }
  };

  const handleOpenReportInGoogleSheets = async () => {
    if (!googleSheetsAction || googleSheetsAction.type !== "open") {
      return;
    }
    setGoogleSheetsError(null);
    setOpeningGoogleSheets(true);
    try {
      await openAssistantGoogleSheetsExportAction(
        googleSheetsAction,
        model.actions.openArtifactInGoogleSheets,
      );
      model.actions.closeExport();
    } catch (error) {
      setGoogleSheetsError(
        error instanceof Error ? error.message : "Google Sheets export failed.",
      );
    } finally {
      setOpeningGoogleSheets(false);
    }
  };

  const renderInsightToneIcon = (
    tone: ReportGenerationWorkspaceModel["insights"][number]["tone"],
  ) => {
    if (tone === "up") {
      return <ArrowUp size={14} className="text-[#28A76F]" />;
    }
    if (tone === "alert") {
      return <AlertTriangle size={14} className="text-[#C57A00]" />;
    }
    return <ArrowDown size={14} className="text-[#CC4444]" />;
  };

  const renderInsightStrip = () => {
    if (insightPresentation !== "info-strip" || model.insights.length === 0) {
      return null;
    }

    return (
      <div
        className={joinClasses(
          "shrink-0 border-b",
          isCompactWorkspaceLayout
            ? "grid grid-cols-1 divide-y px-4 py-2 sm:grid-cols-3 sm:divide-x sm:divide-y-0"
            : "flex items-stretch divide-x px-5 py-2",
          insightStripClassName,
        )}
        data-testid="report-generation-insight-strip"
        data-authority-classification="prototype-authoritative"
        data-authority-source="apps/ystnj/storybook-assets/external-ui/run-reports/src/components/ReportPreview.tsx"
      >
        {model.insights.map((insight) => (
          <article
            key={insight.id}
            className={joinClasses(
              "group flex min-w-0 flex-1 items-center gap-2 transition-colors",
              isCompactWorkspaceLayout
                ? "min-h-12 px-0 py-2 sm:px-4 sm:py-1"
                : "min-h-10 px-4 py-1",
              insightInfoBoxClassName,
            )}
            data-testid="report-generation-insight-card"
            data-report-generation-insight-presentation="info-box"
          >
            <div
              className="flex h-5 w-5 shrink-0 items-center justify-center"
              aria-hidden="true"
            >
              {renderInsightToneIcon(insight.tone)}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className={joinClasses(
                  "line-clamp-2 break-words text-xs font-semibold leading-4",
                  titleInputClassName,
                )}
                title={insight.headline}
              >
                {insight.headline}
              </p>
              <p
                className={joinClasses(
                  "line-clamp-2 break-words text-xs leading-4",
                  secondaryTextClassName,
                )}
                title={insight.detail}
              >
                {insight.detail}
              </p>
            </div>
            <button
              type="button"
              onClick={() => model.actions.dismissInsight(insight.id)}
              className={joinClasses(
                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full opacity-0 transition-[background-color,color,opacity] group-hover:opacity-100 focus-visible:opacity-100",
                insightDismissButtonClassName,
              )}
              aria-label="Dismiss insight"
            >
              <X size={12} />
            </button>
          </article>
        ))}
      </div>
    );
  };

  const renderPreviewLoadingOverlay = () => (
    <div
      className={joinClasses(
        "z-30 flex items-center justify-center overflow-hidden px-4 py-6",
        previewLoadingOverlayStyle
          ? "rounded-none"
          : "absolute inset-0 rounded-xl",
        previewLoadingOverlayClassName,
      )}
      style={previewLoadingOverlayStyle ?? undefined}
      data-testid="report-generation-preview-loading-overlay"
      data-overlay-position={
        previewLoadingOverlayStyle ? "viewport-container" : "content-fallback"
      }
    >
      <ReportGenerationPreviewLoadingArtifact
        active={showPreviewLoadingState}
        surfaceTheme={surfaceTheme}
        statusLabel={
          runReportLoading ? "Running report" : "Refreshing report data"
        }
        detailLabel={
          runReportLoading
            ? "Generating exports and updating history"
            : "Applying latest selection"
        }
        loadingSource={
          runReportLoading
            ? "assistant-report-run"
            : "rtk-domain-router-preview"
        }
      />
    </div>
  );

  const renderPreviewToolbar = () => (
    <div
      className={joinClasses(
        "shrink-0 border-b",
        isCompactWorkspaceLayout
          ? "flex flex-col items-stretch gap-2 px-4 py-3"
          : "flex h-12 items-center gap-3 px-5",
        previewHeaderClassName,
      )}
      data-testid="report-generation-preview-header"
      data-authority-classification="prototype-authoritative"
      data-authority-source="apps/ystnj/storybook-assets/external-ui/run-reports/src/components/ReportPreview.tsx"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <button
          type="button"
          onClick={model.actions.startNewReport}
          className={joinClasses(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-sm transition-colors",
            isDarkSurfaceTheme
              ? "text-[#91A2A6] hover:bg-[#0B1214] hover:text-[#8BC0D2]"
              : "text-[#9CA3AF] hover:bg-[#F4F6F3] hover:text-[#2697A2]",
          )}
          title="Back to home"
          aria-label="Back to home"
          data-testid="report-generation-preview-home-button"
        >
          <Home size={16} />
        </button>
        <input
          value={model.resolvedTitle}
          onChange={(event) => model.actions.setReportTitle(event.target.value)}
          className={joinClasses(
            "min-h-8 min-w-0 max-w-[240px] flex-1 bg-transparent text-sm font-semibold leading-5 outline-none",
            titleInputClassName,
            isDarkSurfaceTheme
              ? "focus:border-[#8BC0D2]"
              : "focus:border-[#2697A2]",
          )}
          style={{ fontFamily: "Mulish" }}
          data-testid="report-generation-title-input"
          aria-label="Report title"
        />
        <span
          className={joinClasses("shrink-0 text-sm", tertiaryTextClassName)}
        >
          {model.totalRowCount} rows
        </span>
        {hiddenColumnCount > 0 ? (
          <button
            type="button"
            onClick={() => {
              for (const fieldId of model.workspace.hiddenColumns) {
                model.actions.toggleHiddenColumn(fieldId);
              }
            }}
            className={joinClasses(
              "flex shrink-0 items-center gap-1 text-xs font-medium transition-colors hover:text-[#2697A2]",
              secondaryTextClassName,
            )}
            data-testid="report-generation-hidden-columns-reset"
          >
            <EyeOff size={14} />
            <span>{hiddenColumnCount} hidden</span>
          </button>
        ) : null}
      </div>

      <div
        className={joinClasses(
          "flex shrink-0 flex-wrap items-center gap-2",
          isCompactWorkspaceLayout ? "w-full" : "",
        )}
        data-testid="report-generation-toolbar"
      >
        <button
          type="button"
          onClick={() => setComparisonOpen((open) => !open)}
          className={joinClasses(
            "inline-flex min-w-0 items-center justify-center gap-2 rounded-sm border px-3 py-2 text-sm font-medium transition-colors",
            comparisonOpen
              ? toolbarAccentButtonClassName
              : toolbarSecondaryButtonClassName,
          )}
          disabled={!model.canPreview}
          aria-pressed={comparisonOpen}
          data-report-generation-action="secondary"
          data-testid="report-generation-compare-button"
        >
          <GitCompareArrows size={14} />
          <span className="truncate">Compare</span>
        </button>
        <button
          type="button"
          onClick={handleToolbarSave}
          className={joinClasses(
            "inline-flex min-w-0 items-center justify-center gap-2 rounded-sm border px-3 py-2 text-sm font-medium transition-colors",
            toolbarSaved
              ? toolbarSavedButtonClassName
              : toolbarSecondaryButtonClassName,
          )}
          disabled={toolbarSaved || model.isSavingTemplate}
          data-report-generation-action="secondary"
          data-testid="report-generation-save-button"
        >
          {toolbarSaved ? <CheckCircle size={14} /> : <Save size={14} />}
          <span className="truncate">{toolbarSaved ? "Saved" : "Save"}</span>
        </button>
        <div
          className={joinClasses(
            "inline-flex min-w-0 items-center rounded-sm border p-0.5",
            bodySurfaceTabsClassName,
          )}
          role="tablist"
          aria-label="Report body view"
          data-testid="report-generation-body-surface-tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeBodySurfaceResolved === "table"}
            onClick={() => setActiveBodySurface("table")}
            className={joinClasses(
              "inline-flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-sm px-2.5 text-sm font-semibold transition-colors",
              activeBodySurfaceResolved === "table"
                ? bodySurfaceTabActiveClassName
                : bodySurfaceTabInactiveClassName,
            )}
            data-report-generation-body-tab="table"
            data-testid="report-generation-body-table-tab"
          >
            <FileSpreadsheet size={14} />
            <span className="truncate">Table</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeBodySurfaceResolved === "visual"}
            onClick={() => setActiveBodySurface("visual")}
            disabled={!visualBodySurfaceAvailable}
            title={
              visualBodySurfaceAvailable
                ? "Open mapped visualization"
                : "No mapped chart policy; table remains active"
            }
            className={joinClasses(
              "inline-flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-sm px-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
              activeBodySurfaceResolved === "visual"
                ? bodySurfaceTabActiveClassName
                : bodySurfaceTabInactiveClassName,
            )}
            data-report-generation-action="accent"
            data-authority-classification="app-runtime-owned"
            data-report-generation-body-tab="visual"
            data-report-generation-visual-policy-id={
              visualPolicy?.id ?? "legacy-implicit"
            }
            data-report-generation-visual-policy-default-body-surface={
              visualPolicyDefaultBodySurface
            }
            data-report-generation-visual-policy-default-chart-option-id={
              visualPolicyDefaultChartOptionId
            }
            data-report-generation-visual-policy-unmapped-fallback={
              visualPolicyUnmappedFallback
            }
            data-report-generation-visual-policy-fallback-reason={
              visualPolicyFallbackReason
            }
            data-report-generation-visual-policy-coverage-signature={
              visualPolicyCoverageSignature
            }
            data-report-generation-visual-policy-graph-view-enabled={String(
              visualPolicyGraphViewEnabled,
            )}
            data-report-generation-visual-policy-graph-default-allowed={String(
              visualPolicy?.graphDefaultAllowed ?? false,
            )}
            data-report-generation-visual-policy-matched-rules={
              visualPolicy?.matchedRuleIds.join("|") ?? ""
            }
            data-testid="report-generation-advanced-visualization-toggle"
          >
            <Network size={14} />
            <span className="truncate">Visual</span>
          </button>
        </div>
        <button
          type="button"
          onClick={model.actions.openExport}
          className={joinClasses(
            "inline-flex min-w-0 items-center justify-center gap-2 rounded-sm border px-3 py-2 text-sm font-medium transition-colors",
            toolbarSecondaryButtonClassName,
          )}
          disabled={exportActionDisabled}
          data-report-generation-action="secondary"
          data-testid="report-generation-export-button"
        >
          <Download size={14} />
          <span className="truncate">Export</span>
        </button>
      </div>
    </div>
  );

  return (
    <div
      ref={workspaceRootRef}
      className={joinClasses(
        "flex h-full min-h-0 w-full min-w-0 overflow-hidden",
        isCompactWorkspaceLayout ? "flex-col" : "flex-row",
        rootClassName,
      )}
      data-testid="report-generation-workspace"
      data-report-generation-surface-theme={surfaceTheme}
      data-report-generation-layout={
        isCompactWorkspaceLayout ? "stacked" : "split"
      }
      data-report-generation-left-panel-collapsed={
        model.workspace.collapsed ? "true" : "false"
      }
    >
      <aside
        className={joinClasses(
          "relative z-10 flex min-h-0 shrink-0 flex-col overflow-visible transition-[width,min-width,height,min-height] duration-200",
          isCompactWorkspaceLayout ? "border-b" : "h-full border-r",
          panelClassName,
        )}
        style={sidePanelStyle}
        data-testid="report-generation-left-panel"
        data-report-generation-left-panel-collapsed={
          model.workspace.collapsed ? "true" : "false"
        }
      >
        <div
          className={joinClasses(
            `flex ${tabsAreStacked ? "flex-col gap-1 p-1" : "flex-row gap-1 px-2 pt-2 pb-2"} border-b`,
            panelTabsBorderClassName,
            panelTabsSurfaceClassName,
          )}
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = model.workspace.activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => model.actions.setActiveTab(tab.id)}
                className={`relative flex min-w-0 items-center justify-center overflow-hidden rounded-sm transition-colors ${
                  tabsAreStacked
                    ? "h-10 w-full gap-1 p-1"
                    : "h-10 flex-1 basis-0 gap-0.5 px-1.5"
                } ${isActive ? activeTabClassName : inactiveTabClassName}`}
                title={model.workspace.collapsed ? tab.label : undefined}
              >
                <Icon size={tabsAreStacked ? 16 : 14} className="shrink-0" />
                {!model.workspace.collapsed || isCompactWorkspaceLayout ? (
                  <span className="min-w-0 truncate text-xs font-medium leading-4">
                    {tab.label}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div
          className={joinClasses(
            "min-h-0 flex-1 overflow-hidden",
            model.workspace.collapsed ? "hidden" : "block",
          )}
          data-testid="report-generation-left-panel-content"
        >
          {!model.workspace.collapsed && model.workspace.activeTab === "templates"
            ? renderTemplateRailContent()
            : null}
          {!model.workspace.collapsed && model.workspace.activeTab === "filters"
            ? renderFiltersContent()
            : null}
          {!model.workspace.collapsed && model.workspace.activeTab === "history"
            ? renderHistoryContent()
            : null}
        </div>

        <button
          type="button"
          onClick={() => model.actions.setCollapsed(!model.workspace.collapsed)}
          className={joinClasses(
            isCompactWorkspaceLayout
              ? "absolute -bottom-4 left-1/2 z-20 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border shadow-sm transition-colors"
              : "absolute -right-4 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border shadow-sm transition-colors",
            collapseButtonClassName,
          )}
          aria-label={
            model.workspace.collapsed ? "Expand panel" : "Collapse panel"
          }
        >
          {model.workspace.collapsed ? (
            <PanelLeftOpen size={14} />
          ) : (
            <PanelLeftClose size={14} />
          )}
        </button>
      </aside>

      <section
        className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        data-testid="report-generation-preview-panel"
      >
        {previewToolbarVisible ? renderPreviewToolbar() : null}
        {renderInsightStrip()}

        {visibleFeedbackMessage ? (
          <div
            role={lastActionIsError ? "alert" : "status"}
            className={joinClasses(
              "pointer-events-none absolute left-1/2 top-4 z-40 flex max-w-[min(520px,calc(100%-32px))] -translate-x-1/2 items-center rounded-lg border px-4 py-2 text-sm font-medium",
              lastActionIsError
                ? feedbackErrorToastClassName
                : feedbackToastClassName,
            )}
            data-testid="report-generation-feedback-toast"
          >
            {visibleFeedbackMessage}
          </div>
        ) : null}

        <div
          ref={previewBodyRef}
          className={joinClasses(
            "min-h-0 flex-1 overflow-y-auto",
            isCompactWorkspaceLayout ? "px-4 py-4" : "px-6 py-5",
          )}
          data-testid="report-generation-preview-body"
          data-report-generation-body-surface={activeBodySurfaceResolved}
          data-loading={previewSurfaceLoading ? "true" : "false"}
          aria-busy={previewSurfaceLoading}
        >
          <div
            className={joinClasses(
              "min-w-0",
              visualBodySurfaceActive
                ? "flex min-h-full flex-col gap-4"
                : "space-y-4",
            )}
          >
            {insightPresentation === "cards" && model.insights.length > 0 ? (
              <div className="grid gap-3 lg:grid-cols-3">
                {model.insights.map((insight) => (
                  <article
                    key={insight.id}
                    className={joinClasses(
                      "rounded-xl border p-4",
                      insightCardClassName,
                    )}
                    data-testid="report-generation-insight-card"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p
                          className={joinClasses(
                            "text-sm font-semibold",
                            titleInputClassName,
                          )}
                        >
                          {insight.headline}
                        </p>
                        <p
                          className={joinClasses(
                            "mt-1 text-xs leading-5",
                            secondaryTextClassName,
                          )}
                        >
                          {insight.detail}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => model.actions.dismissInsight(insight.id)}
                        className={joinClasses(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors",
                          tertiaryTextClassName,
                          isDarkSurfaceTheme
                            ? "hover:bg-[#0B1214] hover:text-[#E8EFEF]"
                            : "hover:bg-[#F4F6F3] hover:text-[#404F4D]",
                        )}
                        aria-label="Dismiss insight"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}

            {comparisonOpen ? (
              <section
                className={joinClasses(
                  "rounded-xl border p-4",
                  previewCardClassName,
                )}
                data-testid="report-generation-compare-panel"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p
                      className={joinClasses(
                        "text-[11px] font-semibold uppercase tracking-[0.16em]",
                        previewEyebrowClassName,
                      )}
                    >
                      Compare
                    </p>
                    <h3
                      className={joinClasses(
                        "mt-1 text-sm font-semibold",
                        titleInputClassName,
                      )}
                    >
                      Current preview versus{" "}
                      {comparisonEntry
                        ? comparisonEntry.templateName
                        : "the next saved run"}
                    </h3>
                    <p
                      className={joinClasses(
                        "mt-1 text-xs leading-5",
                        secondaryTextClassName,
                      )}
                    >
                      {comparisonEntry
                        ? `Reference run: ${comparisonEntry.runDate || "unscheduled"} • ${comparisonEntry.status} • ${comparisonEntry.format}`
                        : "Run or load a report to compare the active preview against saved history."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setComparisonOpen(false)}
                    className={joinClasses(
                      "flex h-8 w-8 items-center justify-center rounded-sm transition-colors",
                      templateActionIconButtonClassName,
                    )}
                    aria-label="Close comparison"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <div
                    className={joinClasses(
                      "rounded-lg border px-3 py-3",
                      templateCardClassName,
                    )}
                  >
                    <p
                      className={joinClasses(
                        "text-[11px] font-semibold uppercase tracking-[0.12em]",
                        tertiaryTextClassName,
                      )}
                    >
                      Rows
                    </p>
                    <p
                      className={joinClasses(
                        "mt-1 text-xl font-semibold",
                        titleInputClassName,
                      )}
                    >
                      {model.totalRowCount.toLocaleString()}
                    </p>
                    <p
                      className={joinClasses(
                        "mt-1 text-xs",
                        secondaryTextClassName,
                      )}
                    >
                      {comparisonRowDelta === null
                        ? "No saved row baseline yet"
                        : comparisonRowDelta === 0
                          ? "Matches selected run"
                          : `${comparisonRowDelta > 0 ? "+" : ""}${comparisonRowDelta.toLocaleString()} versus selected run`}
                    </p>
                  </div>
                  <div
                    className={joinClasses(
                      "rounded-lg border px-3 py-3",
                      templateCardClassName,
                    )}
                  >
                    <p
                      className={joinClasses(
                        "text-[11px] font-semibold uppercase tracking-[0.12em]",
                        tertiaryTextClassName,
                      )}
                    >
                      Visible columns
                    </p>
                    <p
                      className={joinClasses(
                        "mt-1 text-xl font-semibold",
                        titleInputClassName,
                      )}
                    >
                      {comparisonColumnCount.toLocaleString()}
                    </p>
                    <p
                      className={joinClasses(
                        "mt-1 text-xs",
                        secondaryTextClassName,
                      )}
                    >
                      {hiddenColumnCount > 0
                        ? `${hiddenColumnCount} hidden columns excluded`
                        : "All selected columns are visible"}
                    </p>
                  </div>
                  <div
                    className={joinClasses(
                      "rounded-lg border px-3 py-3",
                      templateCardClassName,
                    )}
                  >
                    <p
                      className={joinClasses(
                        "text-[11px] font-semibold uppercase tracking-[0.12em]",
                        tertiaryTextClassName,
                      )}
                    >
                      Reference output
                    </p>
                    <p
                      className={joinClasses(
                        "mt-1 text-xl font-semibold",
                        titleInputClassName,
                      )}
                    >
                      {comparisonEntry?.format ?? "None"}
                    </p>
                    <p
                      className={joinClasses(
                        "mt-1 text-xs",
                        secondaryTextClassName,
                      )}
                    >
                      {comparisonEntry?.artifactRefs.length
                        ? `${comparisonEntry.artifactRefs.length} downloadable artifacts`
                        : "No artifacts attached to the reference run"}
                    </p>
                  </div>
                </div>
              </section>
            ) : null}

            <div className="relative min-h-[260px]">
              {visualBodySurfaceActive && model.advancedVisualization ? (
                <div
                  className="flex min-h-[640px] min-w-0 flex-1"
                  data-testid="report-generation-visual-body-surface"
                >
                  <ReportGenerationAdvancedVisualization
                    snapshot={model.advancedVisualization}
                    onClose={() => setActiveBodySurface("table")}
                    initialView={model.visualizationView}
                    activeView={model.visualizationView}
                    onActiveViewChange={model.actions.setVisualizationView}
                    initialChartType={model.visualizationChartType ?? undefined}
                    activeChartType={model.visualizationChartType ?? undefined}
                    onActiveChartTypeChange={
                      model.actions.setVisualizationChartType
                    }
                    surfaceTheme={surfaceTheme}
                    presentation="workspace"
                  />
                </div>
              ) : (
                <div
                  className={joinClasses(
                    "rounded-xl border",
                    previewCardClassName,
                  )}
                  data-testid="report-generation-preview-card"
                >
                  <div
                    className={joinClasses(
                      "border-b px-4 py-3",
                      isCompactWorkspaceLayout
                        ? "flex flex-col items-start gap-3"
                        : "flex items-center justify-between gap-4",
                      previewTableHeadBorderClassName,
                    )}
                  >
                    <div>
                      <p
                        className={joinClasses(
                          "text-[11px] font-semibold uppercase tracking-[0.16em]",
                          previewEyebrowClassName,
                        )}
                      >
                        Preview
                      </p>
                      <p
                        className={joinClasses(
                          "mt-1 text-xs",
                          secondaryTextClassName,
                        )}
                      >
                        {model.previewColumns.length} visible columns •{" "}
                        {model.totalRowCount} rows • {model.joins.length} joins
                        {runSelected
                          ? ` • loaded from ${runSelected.templateName}`
                          : ""}
                      </p>
                    </div>
                    <div
                      className={joinClasses(
                        "flex flex-wrap items-center gap-2",
                        isCompactWorkspaceLayout ? "w-full" : "",
                      )}
                    >
                      {model.previewColumns.slice(0, 2).map((column) => (
                        <button
                          key={column.fieldId}
                          type="button"
                          onClick={() =>
                            model.actions.toggleHiddenColumn(column.fieldId)
                          }
                          className={joinClasses(
                            "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold",
                            previewChipClassName,
                          )}
                          data-report-generation-ui="preview-column-chip"
                        >
                          <EyeOff size={12} />
                          <span>{column.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {model.previewError ? (
                    <div
                      className="px-5 py-12 text-center"
                      data-testid="report-generation-preview-error-state"
                    >
                      <div
                        className={joinClasses(
                          "mx-auto flex h-14 w-14 items-center justify-center rounded-2xl",
                          previewStateWarningIconClassName,
                        )}
                      >
                        <FileSpreadsheet size={24} />
                      </div>
                      <p
                        className={joinClasses(
                          "mt-4 text-sm font-semibold",
                          titleInputClassName,
                        )}
                      >
                        Preview unavailable
                      </p>
                      <p
                        className={joinClasses(
                          "mt-1 text-xs leading-5",
                          secondaryTextClassName,
                        )}
                      >
                        {model.previewError}
                      </p>
                    </div>
                  ) : !model.canPreview ? (
                    model.hasReportDefinitionSelection ? (
                      <div
                        className="px-5 py-12 text-center"
                        data-testid="report-generation-preview-zero-rows-state"
                      >
                        <div
                          className={joinClasses(
                            "mx-auto flex h-14 w-14 items-center justify-center rounded-2xl",
                            previewStateWarningIconClassName,
                          )}
                        >
                          <AlertTriangle size={24} />
                        </div>
                        <p
                          className={joinClasses(
                            "mt-4 text-sm font-semibold",
                            titleInputClassName,
                          )}
                        >
                          No source-backed rows matched this report
                        </p>
                        <p
                          className={joinClasses(
                            "mx-auto mt-1 max-w-xl text-xs leading-5",
                            secondaryTextClassName,
                          )}
                        >
                          The selected template returned 0 rows for the current
                          filters and year.
                        </p>
                        {model.previewWarnings.length > 0 ? (
                          <div
                            className={joinClasses(
                              "mx-auto mt-4 max-w-2xl rounded-md border px-4 py-3 text-left text-xs font-semibold",
                              previewWarningBannerClassName,
                            )}
                          >
                            {model.previewWarnings.join(" ")}
                          </div>
                        ) : null}
                        {model.previewColumns.length > 0 ? (
                          <div className="mx-auto mt-5 max-w-2xl text-left">
                            <p
                              className={joinClasses(
                                "text-[11px] font-semibold uppercase tracking-[0.16em]",
                                tertiaryTextClassName,
                              )}
                            >
                              Selected columns
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {model.previewColumns.map((column) => (
                                <span
                                  key={column.fieldId}
                                  className={joinClasses(
                                    "inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-[11px] font-semibold",
                                    previewChipClassName,
                                  )}
                                >
                                  <span className="truncate">
                                    {column.label}
                                  </span>
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="px-5 py-12 text-center">
                        <div
                          className={joinClasses(
                            "mx-auto flex h-14 w-14 items-center justify-center rounded-2xl",
                            previewStateAccentIconClassName,
                          )}
                        >
                          <FileSpreadsheet size={24} />
                        </div>
                        <p
                          className={joinClasses(
                            "mt-4 text-sm font-semibold",
                            titleInputClassName,
                          )}
                        >
                          Select a template or build a report
                        </p>
                        <p
                          className={joinClasses(
                            "mt-1 text-xs leading-5",
                            secondaryTextClassName,
                          )}
                        >
                          Choose a template from the Gallery, or start from
                          scratch using the Filters tab.
                        </p>
                        <div className="mx-auto mt-5 grid max-w-2xl gap-2 sm:grid-cols-2">
                          {model.galleryTemplates
                            .slice(0, 4)
                            .map((template) => (
                              <button
                                key={template.id}
                                type="button"
                                onClick={() =>
                                  model.actions.applyTemplateById(
                                    template.id,
                                    "gallery",
                                  )
                                }
                                className={joinClasses(
                                  "rounded-lg border px-3 py-2.5 text-left transition-colors",
                                  templateCardClassName,
                                )}
                                data-testid="report-generation-empty-template-shortcut"
                              >
                                <p
                                  className={joinClasses(
                                    "truncate text-sm font-semibold",
                                    titleInputClassName,
                                  )}
                                >
                                  {template.name}
                                </p>
                                <p
                                  className={joinClasses(
                                    "mt-1 truncate text-[11px]",
                                    secondaryTextClassName,
                                  )}
                                >
                                  {template.moduleLabel}
                                </p>
                              </button>
                            ))}
                        </div>
                      </div>
                    )
                  ) : (
                    <div
                      className="relative min-h-0 overflow-auto"
                      data-testid="report-generation-preview-scroll-region"
                      tabIndex={model.previewColumns.length > 4 ? 0 : undefined}
                      aria-label={
                        model.previewColumns.length > 4
                          ? "Report preview table. Scroll horizontally to view all columns."
                          : "Report preview table"
                      }
                    >
                      {model.previewWarnings.length > 0 ? (
                        <div
                          className={joinClasses(
                            "border-b px-4 py-3 text-xs font-semibold",
                            previewWarningBannerClassName,
                          )}
                        >
                          {model.previewWarnings.join(" ")}
                        </div>
                      ) : null}
                      {model.previewColumns.length > 4 ? (
                        <p
                          className={joinClasses(
                            "sticky left-0 z-10 border-b px-4 py-2 text-xs font-medium",
                            previewTableHeadCellClassName,
                            previewTableHeadBorderClassName,
                          )}
                          data-testid="report-generation-horizontal-scroll-hint"
                        >
                          Scroll horizontally to view all {model.previewColumns.length} columns.
                        </p>
                      ) : null}
                      <table
                        className="w-max min-w-full table-fixed border-collapse"
                        data-testid="report-generation-preview-table"
                      >
                        <thead>
                          <tr
                            className={joinClasses(
                              "border-b",
                              previewTableHeadBorderClassName,
                            )}
                          >
                            {model.previewColumns.map((column, columnIndex) => (
                              <th
                                key={column.fieldId}
                                className={joinClasses(
                                  "min-w-44 max-w-44 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em]",
                                  columnIndex === 0 ? "sticky left-0 z-10" : "",
                                  previewTableHeadCellClassName,
                                )}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      model.actions.toggleSort(column.fieldId)
                                    }
                                    className="inline-flex min-w-0 items-start gap-2 text-left leading-4"
                                  >
                                    <span className="break-words normal-case">{column.label}</span>
                                    {model.workspace.sortColumn ===
                                    column.fieldId ? (
                                      model.workspace.sortDirection ===
                                      "asc" ? (
                                        <ArrowUp size={12} />
                                      ) : (
                                        <ArrowDown size={12} />
                                      )
                                    ) : null}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      model.actions.setContextMenuField(
                                        model.workspace.contextMenuFieldId ===
                                          column.fieldId
                                          ? null
                                          : column.fieldId,
                                      )
                                    }
                                    className={joinClasses(
                                      "rounded-sm p-1 transition-colors",
                                      tertiaryTextClassName,
                                      isDarkSurfaceTheme
                                        ? "hover:bg-[#10181B] hover:text-[#E8EFEF]"
                                        : "hover:bg-[#EDF2F7] hover:text-[#404F4D]",
                                    )}
                                    aria-label={`Column actions for ${column.label}`}
                                  >
                                    <MoreVertical size={14} />
                                  </button>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {model.previewRows.map((row) => (
                            <tr
                              key={row.id}
                              className={joinClasses(
                                "border-b last:border-b-0",
                                previewRowBorderClassName,
                              )}
                            >
                              {model.previewColumns.map((column, columnIndex) => (
                                <td
                                  key={column.fieldId}
                                  className={joinClasses(
                                    "min-w-44 max-w-44 break-words px-4 py-3 text-sm leading-5",
                                    columnIndex === 0
                                      ? isDarkSurfaceTheme
                                        ? "sticky left-0 z-[5] bg-[#060B0C]"
                                        : "sticky left-0 z-[5] bg-white"
                                      : "",
                                    previewCellTextClassName,
                                  )}
                                >
                                  {formatPreviewCellValue(
                                    row.cells[column.fieldId],
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div
                        className={joinClasses(
                          "flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-[11px]",
                          previewTableHeadBorderClassName,
                          secondaryTextClassName,
                        )}
                      >
                        <span>
                          Showing {model.previewRows.length.toLocaleString()}
                          {model.totalRowCount !== model.previewRows.length
                            ? ` of ${model.totalRowCount.toLocaleString()}`
                            : ""}{" "}
                          rows
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <MoreVertical size={12} />
                          Use column menus for options
                        </span>
                      </div>

                      {model.workspace.contextMenuFieldId ? (
                        <div
                          className={joinClasses(
                            "absolute right-4 top-4 z-20 w-52 rounded-lg border py-1",
                            previewColumnMenuClassName,
                          )}
                          data-testid="report-generation-column-menu"
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (model.workspace.contextMenuFieldId) {
                                model.actions.toggleHiddenColumn(
                                  model.workspace.contextMenuFieldId,
                                );
                              }
                              model.actions.setContextMenuField(null);
                            }}
                            className={joinClasses(
                              "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                              previewColumnMenuButtonClassName,
                            )}
                          >
                            <EyeOff size={14} />
                            <span>Hide {openColumnMenuLabel ?? "column"}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              model.actions.setContextMenuField(null)
                            }
                            className={joinClasses(
                              "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                              previewColumnMenuButtonClassName,
                            )}
                          >
                            <X size={14} />
                            <span>Close menu</span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
              {showPreviewLoadingState && !model.previewError
                ? renderPreviewLoadingOverlay()
                : null}
            </div>
          </div>
        </div>

        {model.workspace.showExport ? (
          <div
            className={joinClasses(
              "absolute z-30 rounded-lg border p-4",
              isCompactWorkspaceLayout
                ? "left-4 right-4 top-[120px] w-auto"
                : "right-6 top-[56px] w-72",
              exportMenuClassName,
            )}
            data-testid="report-generation-export-menu"
          >
            <p
              className={joinClasses(
                "mb-3 text-lg font-semibold",
                titleInputClassName,
              )}
              style={{ fontFamily: "Mulish" }}
            >
              Export report
            </p>
            <div className="space-y-1.5">
              {googleSheetsAction ? (
                <AssistantGoogleSheetsExportActionItem
                  action={googleSheetsAction}
                  opening={openingGoogleSheets}
                  disabled={exportActionDisabled}
                  onOpen={() => {
                    if (googleSheetsAction.type === "connect") {
                      return;
                    }
                    void handleOpenReportInGoogleSheets();
                  }}
                  buttonClassName={joinClasses(
                    "flex w-full items-center justify-between gap-3 rounded-md border border-transparent px-3 py-2.5 text-left transition-colors",
                    exportOptionClassName,
                  )}
                  testId="report-generation-open-google-sheets-button"
                />
              ) : null}
              {EXPORT_FORMAT_OPTIONS.map((option) => {
                const isSelected = selectedExportFormat === option.format;
                return (
                  <button
                    key={option.format}
                    type="button"
                    onClick={() => setSelectedExportFormat(option.format)}
                    aria-pressed={isSelected}
                    className={joinClasses(
                      "w-full rounded-md border px-3 py-2.5 text-left transition-colors",
                      exportOptionClassName,
                      isSelected
                        ? exportOptionSelectedClassName
                        : "border-transparent",
                    )}
                  >
                    <p
                      className={joinClasses(
                        "text-sm font-medium",
                        titleInputClassName,
                      )}
                    >
                      {option.label}
                    </p>
                    <p
                      className={joinClasses(
                        "text-[11px]",
                        secondaryTextClassName,
                      )}
                    >
                      {option.description}
                    </p>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={handlePrintReport}
                className={joinClasses(
                  "flex w-full items-center gap-3 rounded-md border border-transparent px-3 py-2.5 text-left transition-colors",
                  exportOptionClassName,
                )}
                disabled={exportActionDisabled}
                data-testid="report-generation-print-button"
              >
                <Printer size={16} className="shrink-0 text-[#9CA3AF]" />
                <span className="min-w-0 flex-1">
                  <span
                    className={joinClasses(
                      "block text-sm font-medium",
                      titleInputClassName,
                    )}
                  >
                    Print
                  </span>
                  <span
                    className={joinClasses(
                      "block text-[11px]",
                      secondaryTextClassName,
                    )}
                  >
                    Send the current view to printer
                  </span>
                </span>
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={model.actions.closeExport}
                className={joinClasses(
                  "flex-1 rounded-sm border px-3 py-2 text-sm font-medium transition-colors",
                  exportCancelButtonClassName,
                )}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => model.actions.runExport(selectedExportFormat)}
                className={joinClasses(
                  "flex-1 rounded-sm px-3 py-2 text-sm font-semibold transition-colors",
                  toolbarPrimaryButtonClassName,
                )}
                disabled={exportActionDisabled}
                data-testid="report-generation-run-export-button"
              >
                Generate export
              </button>
            </div>
            <button
              type="button"
              onClick={model.actions.runExportBundle}
              className={joinClasses(
                "mt-2 w-full rounded-sm border px-3 py-2 text-sm font-medium transition-colors",
                runFullBundleButtonClassName,
              )}
              disabled={exportActionDisabled}
              data-testid="report-generation-run-full-bundle-button"
            >
              Run full bundle
            </button>
            {googleSheetsError ? (
              <p
                className={joinClasses("mt-2 text-xs", secondaryTextClassName)}
                role="status"
              >
                {googleSheetsError}
              </p>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
